/** What each AutoCAD command actually DOES here — the one place the mode is wired.
 *
 * ⚠⚠ **AN ABSENT HANDLER IS THE GREY.** `AutoCadShell` draws an item live exactly when this
 * table gives it a `run` (or a `select`/`slider`), so the way to grey something is to not wire
 * it, and the way to light it up is to wire it — in one edit, in one file. There is no second
 * "implemented" list to fall out of step, which is the failure that produces a button that looks
 * alive and silently does nothing. The ribbon, the pull-down menus, the classic toolbars, the
 * status bar and the COMMAND LINE all read this table, so none of them can disagree.
 *
 * ⚠⚠⚠ **THIS FILE WAS LIFTED FROM THE DESIGN APP AND ITS NOTES DESCRIBED THAT PRODUCT.** The
 * paragraph that used to sit here said a third of the grey is grey "only because the feature
 * lives somewhere else: the 3D tools are built and working in the 3D view, the sheets are
 * composed on the drawing's data sheet, the wall and the staircase are on the PDVN tab." None of
 * those places exists in pdcad. `package.json` has no three.js, the canvas is a 2D context, and
 * the PDVN tab was removed — measured, 17 of its 38 controls had no handler and the rest armed
 * modes this studio does not have. A note pointing somewhere that is not there is worse than no
 * note: it tells a drafter the app CAN do something and sends them to look for it.
 *
 * ⚠⚠ **`note` IS NOT DECORATION — IT IS THE DIFFERENCE BETWEEN "MISSING" AND "NOT COMING".** A
 * grey that says only "not built yet" names nothing and expires never. A good one says what
 * would have to exist first, what this product does instead, or plainly that it will never
 * apply — and the good ones name their own expiry date, which is how POINT got built.
 * ⚠ And a LIVE command gets a note too when it does something the label does not say.
 *
 * ⚠⚠ **WHERE AUTOCAD'S MEANING AND OURS DIFFER, THE COMMAND IS GREY, NOT APPROXIMATED.** The
 * clearest case here is the word COPY, which AutoCAD uses for two different commands: the Modify
 * one duplicates at a displacement (that IS our `copy` edit-op, so it is live) and COPYCLIP is a
 * clipboard we do not have (so it is grey). Wiring them to each other would give a drafter a
 * Ctrl+C that quietly drew a second wall 200 mm to the right. Silently-different behaviour is
 * worse than a grey button, because it is discovered after the drawing is wrong.
 */
import { WS_NAME } from "./AutoCadShell";
import { LINEWEIGHTS, LTYPE_DASH, PDMODES } from "./acadtables";
import type { AcadHandler, AcadHandlers } from "./AutoCadShell";
import { OSNAP_MODES, VAR_CMDS } from "./catalog";

/** Said the same way every time, so a person learns to recognise each class at a glance. */
const SOON = "not built yet";

/** AutoCAD's seven index colours, by the names a drafter says out loud.
 *
 * ⚠ Seven rather than a full picker: these are the ones the office's pen table is built around,
 * and a drawing where every object is a different hand-picked colour prints as mud. A hex value
 * typed into `props.colour` still works — this list is the fast path, not the only one.
 */
const PEN_COLOURS: [string, string][] = [
  ["#e11d48", "Red"], ["#eab308", "Yellow"], ["#16a34a", "Green"], ["#06b6d4", "Cyan"],
  ["#2563eb", "Blue"], ["#c026d3", "Magenta"], ["#16181a", "Black/White"],
];


/** WHY each unbuilt command is unbuilt — the reasons, in one table.
 *
 * ⚠⚠ **A GREY BUTTON SAYING ONLY "not built yet" TEACHES NOBODY ANYTHING.** 116 of them
 * did, which is half of every grey in the mode. The good ones already showed what the
 * difference is: `osnap.node`'s grey said *"AutoCAD's Node catches POINT objects, and
 * POINT is not built"* — and that sentence named its own expiry date, so somebody built
 * POINT and the grey went away. "Not built yet" names nothing and expires never.
 *
 * ⚠ So each reason below says one of three things: what this product does INSTEAD (and
 * where), what would have to exist first, or why it will never apply here. A few say
 * plainly that the command is worth building — MATCHPROP, QSELECT, FIND — which is more
 * useful to the next session than any of them being quietly identical.
 *
 * ⚠ Kept as ONE table rather than beside each handler on purpose: read together, it is
 * the honest shape of the gap between this product and AutoCAD, and that is a thing worth
 * being able to read in one go.
 */
const WHY: Record<string, string> = {
  "addin.appmanager": "there is no add-in system, deliberately — this app is not extensible from outside",
  "addin.appstore": "there is no add-in system, deliberately — this app is not extensible from outside",
  "addin.plugins": "there is no add-in system, deliberately — this app is not extensible from outside",
  "anno.addscale": "an annotative object carries several sizes, one per plot scale — and THIS DRAWING IS ALREADY ANNOTATIVE, which is why there is no list to manage. A note's height is in PAPER millimetres and a viewport's plot scale never touches it: measured on one drawing in three viewports, an 8 m wall drew 400, 160 and 80 mm at 1:20, 1:50 and 1:100 while the note stayed 2.2 mm in all three. Every object supports every scale, so there is nothing to add, reset, monitor or make visible",
  "anno.syncscale": "an annotative object carries several sizes, one per plot scale — and THIS DRAWING IS ALREADY ANNOTATIVE, which is why there is no list to manage. A note's height is in PAPER millimetres and a viewport's plot scale never touches it: measured on one drawing in three viewports, an 8 m wall drew 400, 160 and 80 mm at 1:20, 1:50 and 1:100 while the note stayed 2.2 mm in all three. Every object supports every scale, so there is nothing to add, reset, monitor or make visible",
  "anno.visibility": "an annotative object carries several sizes, one per plot scale — and THIS DRAWING IS ALREADY ANNOTATIVE, which is why there is no list to manage. A note's height is in PAPER millimetres and a viewport's plot scale never touches it: measured on one drawing in three viewports, an 8 m wall drew 400, 160 and 80 mm at 1:20, 1:50 and 1:100 while the note stayed 2.2 mm in all three. Every object supports every scale, so there is nothing to add, reset, monitor or make visible",
  "anno.style.table": "a TABLE STYLE names the fonts, borders and row heights a table object is drawn with, and a schedule here is generated linework rather than a table object — so there is no style for one to carry. ⚠ Pen, colour and weight are set per LAYER in the Layer Properties Manager or per OBJECT on the Properties panel; this is not that",
  "anno.table": "an ACAD_TABLE is an editable grid of cells with its own style, and there is no such entity here — in the codec, in the writer or on the canvas. ⚠⚠ Measured across the whole office archive — 17,903 readable drawings — ACAD_TABLE appears NOT ONCE: every schedule in this office is drawn as TEXT and LINES, which MTEXT, LINE and COPY already make. ⚠ Its button used to say it placed a door and window schedule as linework; it armed a mode nothing answered and did nothing at all, which is the worse of the two possible wrongs",
  "draw.measurepts": "AutoCAD's MEASURE drops point markers at a fixed interval along an object and leaves it whole. Nothing here places markers along a curve yet — the same gap DIVIDE has, and the two are one job",
  "anno.leader.remove": "removes one leader line from a MULTILEADER that has several — and it would change the file and NOT the drawing, for the reason ADD LEADER gives at length: a client's MULTILEADER is drawn from its proxy-graphics blob, which this app can read and cannot regenerate",
  "anno.table.link": "an ACAD_TABLE object, linked to a spreadsheet. ⚠ Measured across the whole office archive — 17,903 readable drawings — ACAD_TABLE appears NOT ONCE, so there is no table object here for a link to hang off. Schedules in this office are drawn as text and lines",
  "anno.text.spell": "no dictionary — and these drawings are trilingual VN/EN/中文, so it would need three",
  "block.setbase": "BASE sets the insertion point of the DRAWING, for when it is itself inserted as a block. Nothing here inserts one drawing into another yet — an xref is attached, not inserted",
  "block.favourites": "the block list comes from THIS DRAWING's own block table, with a usage count, and it is sorted by use — which is the same job a favourites list does without a second list to keep",
  "block.recent": "the block list comes from THIS DRAWING's own block table, with a usage count, and it is sorted by use — which is the same job a recent list does without a second list to keep",
  "col.share": "the drawing is rows in Postgres, not a file to send, compare or mark up. ⚠ What exists instead is UNDO, REDO and RESTORE — a journal of every edit, not a diff between two drawings",
  "col.compare": "the drawing is rows in Postgres, not a file to send, compare or mark up. ⚠ What exists instead is UNDO, REDO and RESTORE — a journal of every edit, not a diff between two drawings",
  "col.xrefcompare": "the drawing is rows in Postgres, not a file to send, compare or mark up. ⚠ What exists instead is UNDO, REDO and RESTORE — a journal of every edit, not a diff between two drawings",
  "col.trace": "the drawing is rows in Postgres, not a file to send, compare or mark up. ⚠ What exists instead is UNDO, REDO and RESTORE — a journal of every edit, not a diff between two drawings",
  "col.markupimport": "the drawing is rows in Postgres, not a file to send, compare or mark up. ⚠ What exists instead is UNDO, REDO and RESTORE — a journal of every edit, not a diff between two drawings",
  "draw.3dpoly": "this app is 2D — one plane, a 2D canvas context, and no 3D viewer of any kind. A 3D polyline can be READ, carried and written back out; it cannot be drawn here",
  "draw.helix": "this app is 2D — one plane, a 2D canvas context, and no 3D viewer of any kind. ⚠ And HELIX appears NOT ONCE across the whole office archive, 17,903 readable drawings",
  "file.audit": "there is no file to audit or recover — the drawing is rows in Postgres, and every edit is journalled for UNDO and RESTORE",
  "file.recover": "there is no file to audit or recover — the drawing is rows in Postgres, and every edit is journalled for UNDO and RESTORE",
  "file.archive": "packaging a drawing with everything it references. Everything here is already in one database, so there is nothing to gather up",
  "file.etransmit": "packaging a drawing with everything it references. Everything here is already in one database, so there is nothing to gather up",
  "file.saveas.template": "no templates — a new drawing starts from the house layer set, which is the same thing without a file",
  "file.open.dgn": "DWG and DXF are read — DWG through LibreDWG in its own process — and DXF is written back. DGN, and every other exchange format, is not",
  "format.limits": "LIMITS bounds ZOOM ALL. This canvas zooms to what is actually drawn, which is the answer LIMITS exists to approximate",
  "cloud.open": "there is no cloud store — the drawing is on this server, and it is already saved",
  "help.help": "no help system — every button's tooltip says what it does and, when it is grey, why",
  "help.newfeatures": "no help system — every button's tooltip says what it does and, when it is grey, why",
  "help.support": "no help system — every button's tooltip says what it does and, when it is grey, why",
  "insert.datalink": "links a table object to a spreadsheet, and there is no table object here to link — ACAD_TABLE appears not once in 17,903 office drawings",
  "mod.clip": "⚠ THIS NOTE SAID \"neither is placed here\" AND BOTH NOW ARE — images attach and xrefs resolve, and an XCLIP boundary read from a client DWG is honoured on screen, inverted clips included. What is missing is only the command that DRAWS a new boundary interactively",
  "mod.object": "edits a placed image, xref or underlay. IMAGEADJUST, xref binding and clipping exist; a single OBJECT command that dispatches to whichever one you picked does not",
  "out.exportsetup": "plot settings are remembered per drawing in the Plot dialog — there is no named page setup to export",
  "out.viewdetails": "lists what a sheet contains before plotting. The Plot dialog previews the real thing instead",
  "pal.designcenter": "DESIGNCENTER browses OTHER drawings for content to copy. Worth building — every block in this office lives in a drawing somebody else made — and it needs cross-drawing block search, which does not exist yet",
  "pal.markup": "no markup sets — comments go on the drawing itself, and everyone is in it at once",
  "qat.below": "the Quick Access Toolbar is fixed — it cannot be moved below the ribbon or added to",
  "qat.more": "the Quick Access Toolbar is fixed — it cannot be moved below the ribbon or added to",
  "st.annomon": "an annotative object carries several sizes, one per plot scale — and THIS DRAWING IS ALREADY ANNOTATIVE, which is why there is no list to manage. A note's height is in PAPER millimetres and a viewport's plot scale never touches it: measured on one drawing in three viewports, an 8 m wall drew 400, 160 and 80 mm at 1:20, 1:50 and 1:100 while the note stayed 2.2 mm in all three. Every object supports every scale, so there is nothing to add, reset, monitor or make visible",
  "st.annovis": "an annotative object carries several sizes, one per plot scale — and THIS DRAWING IS ALREADY ANNOTATIVE, which is why there is no list to manage. A note's height is in PAPER millimetres and a viewport's plot scale never touches it: measured on one drawing in three viewports, an 8 m wall drew 400, 160 and 80 mm at 1:20, 1:50 and 1:100 while the note stayed 2.2 mm in all three. Every object supports every scale, so there is nothing to add, reset, monitor or make visible",
  "st.autoscale": "an annotative object carries several sizes, one per plot scale — and THIS DRAWING IS ALREADY ANNOTATIVE, which is why there is no list to manage. A note's height is in PAPER millimetres and a viewport's plot scale never touches it: measured on one drawing in three viewports, an 8 m wall drew 400, 160 and 80 mm at 1:20, 1:50 and 1:100 while the note stayed 2.2 mm in all three. Every object supports every scale, so there is nothing to add, reset, monitor or make visible",
  "st.gfx": "there is nothing to tune — the drawing is a 2D canvas context and there is no hardware pipeline behind it to configure",
  "st.lockui": "nothing in this shell floats, so there is nothing to lock in place",
  "ui.lock.all": "nothing in this shell floats, so there is nothing to lock in place",
  "ui.lock.toolbars": "nothing in this shell floats, so there is nothing to lock in place",
  "ui.lock.windows": "nothing in this shell floats, so there is nothing to lock in place",
  "ws.settings": "a workspace here IS its ribbon tabs, and those are fixed per workspace — there is nothing of your own in one to configure",
  "util.addselected": "starts the command that drew the object you picked. Nothing maps an object back to its own tool yet",
  "util.calc": "type lengths and angles straight into the command bar's Distance and Angle boxes — they take arithmetic",
  "util.measure.volume": "volume needs a solid, and this app is 2D. DIST and AREA are live; nothing here has a third dimension to measure",
  // ⚠ Grey since these took AutoCAD's own ids — see `mod.solidedit` in `catalog.ts`. Region
  // booleans are the one 2D use AutoCAD makes of them, and REGION is exactly what this app
  // carries without decomposing, so the note names the blocker rather than the feature.
  "mod.union": "UNION, SUBTRACT and INTERSECT combine REGIONs. A REGION is read here and written back untouched, but its boundary lives in an ACIS payload this app does not decode — so there is no outline to combine. BOUNDARY and HATCH are the 2D route",
  "mod.subtract": "UNION, SUBTRACT and INTERSECT combine REGIONs. A REGION is read here and written back untouched, but its boundary lives in an ACIS payload this app does not decode — so there is no outline to combine. BOUNDARY and HATCH are the 2D route",
  "mod.intersect": "UNION, SUBTRACT and INTERSECT combine REGIONs. A REGION is read here and written back untouched, but its boundary lives in an ACIS payload this app does not decode — so there is no outline to combine. BOUNDARY and HATCH are the 2D route",
  "mod.divide": "AutoCAD's DIVIDE drops point markers at equal intervals along an object and leaves it whole. Nothing here places markers along a curve yet",
  "mod.align": "ALIGN moves and rotates a selection so two of its points land on two others \u2014 a MOVE, a ROTATE and optionally a SCALE decided together. There is no server op for it; the three separately do the same job today",
  "mod.lengthen": "LENGTHEN changes an object's length from either end by a distance, a percentage or a total. STRETCH does the same job by catching one end in a crossing window, and is built",
  "util.purge": "every drawing carries the WHOLE house layer and style set whether it uses them or not \u2014 measured 2026-09-02: 527 of 1,459 styles and 415 layers across 60 drawings are unused. That is the office standard, not debris, and purging it would strip a drawing of the conventions the next person expects to find. Blocks come from the catalogue and are never copied in",
};


/** The 3D tabs' reasons, and they are THREE reasons rather than sixty: there is no solid
 *  kernel, no surface modeller and no editable mesh. Saying each once is the point. */
/** Why each Solid-tab command is still grey — one reason per command, not one for the tab.
 *
 * ⚠⚠ **REWRITTEN 2026-09-01 after `DesignSolidOp` landed.** Every entry here used to say the
 * same thing: "the 3D model is BUILT FROM THE PLAN, so there is no free-standing solid to apply
 * this to". That was one sentence covering twenty different commands, and it stopped being true
 * for half of them the day the Solid Tools shipped — UNION, SUBTRACT and INTERSECT now cut real
 * solids in the 3D view, and INTERFERE reads them. A tooltip that is out of date is worse than
 * a blunt one: it is the reason twenty commands looked impossible when three of them were
 * already built one layer down.
 */
const SOLID_WHY: Record<string, string> = {
  // ⚠⚠⚠ **EVERY VALUE IN THIS TABLE DESCRIBED THE DESIGN APP AND NOT ONE OF THEM WAS TRUE
  // HERE.** They spoke of "every body in this model is a SOLID derived from a record (a wall has
  // a length and a thickness)", meshes "parsed from .skp files or generated by the parametric
  // builder", materials "from the catalogue — 548 of them, 485 An Cường", and a render page
  // with a live sun. pdcad has no model, no kernel, no .skp parser, no material catalogue and no
  // renderer: `package.json` has no three.js and the canvas is a 2D context. A grey button
  // explaining itself with another product's architecture teaches a drafter something false.
  //
  // ⚠ **EVERY KEY IS KEPT.** Its predecessor's own warning, from 2026-09-01: rewriting half this
  // table dropped fifty keys in one edit and the DOM census caught it the same minute —
  // "0 reasonless controls -> 37". The reasons are now one per FAMILY, said once each, because
  // for this app they genuinely are one reason each.
  "sol.extractedges": "a SOLID modelling command. pdcad is 2D — the document stores full 3D coordinates and carries ACIS payloads through a round trip untouched, but there is no modelling kernel and nothing to display one in",
  "sol.offsetedge": "a SOLID modelling command. pdcad is 2D — the document stores full 3D coordinates and carries ACIS payloads through a round trip untouched, but there is no modelling kernel and nothing to display one in",
  "surf.trim": "a NURBS SURFACE command. pdcad is 2D and has no surface geometry. ⚠ Measured over the whole office archive, 17,903 readable drawings: all seven SURFACE kinds appear NOT ONCE, so this is off the path by the archive as well as by the design",
  "surf.untrim": "a NURBS SURFACE command. pdcad is 2D and has no surface geometry. ⚠ Measured over the whole office archive, 17,903 readable drawings: all seven SURFACE kinds appear NOT ONCE, so this is off the path by the archive as well as by the design",
  "surf.project": "a NURBS SURFACE command. pdcad is 2D and has no surface geometry. ⚠ Measured over the whole office archive, 17,903 readable drawings: all seven SURFACE kinds appear NOT ONCE, so this is off the path by the archive as well as by the design",
  "surf.curvature": "a NURBS SURFACE command. pdcad is 2D and has no surface geometry. ⚠ Measured over the whole office archive, 17,903 readable drawings: all seven SURFACE kinds appear NOT ONCE, so this is off the path by the archive as well as by the design",
  "mesh.box": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.cone": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.cylinder": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.pyramid": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.sphere": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.wedge": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.torus": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.revsurf": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.edgesurf": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.rulesurf": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.smoothmore": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.smoothless": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.refine": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.crease": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.uncrease": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.split": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "mesh.smoothobj": "an editable subdivision MESH. pdcad is 2D and has no mesh editing. ⚠ Measured over 17,903 office drawings: MESH is in 0.3% of them, and it is carried through a round trip rather than edited",
  "vis.sun": "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D drafting app — no 3D geometry to render and no renderer. Its output is a plotted PDF at true scale and a DXF",
  "vis.geoloc": "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D drafting app — no 3D geometry to render and no renderer. Its output is a plotted PDF at true scale and a DXF",
  "vis.matbrowser": "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D drafting app — no 3D geometry to render and no renderer. Its output is a plotted PDF at true scale and a DXF",
  "vis.mateditor": "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D drafting app — no 3D geometry to render and no renderer. Its output is a plotted PDF at true scale and a DXF",
  "vis.matattach": "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D drafting app — no 3D geometry to render and no renderer. Its output is a plotted PDF at true scale and a DXF",
  "vis.rendercrop": "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D drafting app — no 3D geometry to render and no renderer. Its output is a plotted PDF at true scale and a DXF",
  "vis.renderwin": "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D drafting app — no 3D geometry to render and no renderer. Its output is a plotted PDF at true scale and a DXF",
  "vis.renderenv": "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D drafting app — no 3D geometry to render and no renderer. Its output is a plotted PDF at true scale and a DXF",
  "vis.renderpresets": "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D drafting app — no 3D geometry to render and no renderer. Its output is a plotted PDF at true scale and a DXF",
  "view.camera": "CAMERA saves a viewpoint as an OBJECT you can select and drag. The View Manager (V) saves the same viewpoint by name, which is the part a 2D drafting app can honour",
};

/** AutoCAD's POLARANG list, on the polar button's drop-up. */
export const POLAR_ANGLES = [90, 45, 30, 22.5, 15, 10, 5];


// ⚠⚠ `IN_3D` — "built, open it in the 3D view (the drawing's data sheet ▸ 3D model)" — IS GONE,
// and its removal is the point. Once the 3D view arrived INSIDE this mode, every note carrying
// it became a signpost to a place the user was already standing. Worse, two of them (FBX and
// STL export) had been promising a feature the 3D view never had at all. A where-to-find-it note
// is only worth having while it is true; when the geography changes, it has to be re-read line
// by line, not left to rot. Six of them were, on 2026-08-31.
// `ONE_CAMERA` went the same way: the plan canvas no longer has one camera.
// ⚠⚠ **IT USED TO SAY "this variant is not [built]" AND ALL THREE VARIANTS ARE.** Paste
// Special, Paste as Block and Paste to Original Coordinates each have a `run` a few screens
// down; they are grey only because the clipboard is EMPTY, which is a fact about this instant
// and not about the command. The old string was left behind when they were wired, so the three
// of them spent their lives announcing that they did not exist — a stale grey reason is exactly
// as wrong as a button that does nothing, pointing the other way.
const NO_CLIP = "the clipboard is empty";
// ⚠⚠ **THIS SAID "sheets are composed on the drawing's data sheet ▸ Sheets"** — a screen in the
// DESIGN app. pdcad composes sheets the AutoCAD way: LAYOUTs along the bottom of the drawing
// area, viewports cut with MVIEW, and PLOT writing a PDF at true scale. What is missing is only
// AutoCAD's Sheet SET — a named collection spanning several drawings.
const ON_SHEET = "a sheet here is a LAYOUT — the tabs under the drawing. Cut a viewport with "
  + "MVIEW and plot it with PLOT, which writes a PDF at true scale. A named sheet SET spanning "
  + "several drawings is not built";
const IN_LAYER_PAL = "layers are managed in the Layer Properties Manager palette";
/** ⚠ There is no light RECORD in pdcad — `drawing/models.py` has no such model — and no
 *  renderer to use one. Said once for the four buttons on the Visualise tab. */
const NO_LIGHTS =
  "not built — pdcad is a 2D drafting app with no renderer and no light record of any kind. "
  + "These arrived with the lifted ribbon; the 3D view shades with a fixed headlight";

/** The studio's own tool id for an AutoCAD command, where the GESTURE genuinely matches.
 *
 * ⚠ Every pair here was checked pick by pick, not by name. `DIMLINEAR` is in it because its
 * three picks are ours exactly — first extension line origin, second origin, then pull the
 * dimension line clear. `QDIM` is not, because it dimensions a whole selection at once. */
/** ⚠⚠ **THE LIMIT, SAID ONCE, IN ONE PLACE.** Six handlers on this tab need the same sentence,
 *  and six copies of a caveat drift apart until one of them is a lie. This one replaced
 *  `NO_PARAMETRICS`, which said the dimensional half was not built — true until migration 0010
 *  and `backend/drawing/parametric.py`, and kept honest here by changing the words rather than
 *  deleting them.
 *  ⚠ What is built: a stored relationship and a way to RE-APPLY it. What is not: a solver. */
const NOT_A_SOLVER =
  "⚠ This is not a solver: nothing is maintained continuously, so dragging a constrained object "
  + "by hand will NOT drag what is constrained to it. The Measured column shows you when that "
  + "has happened";

/** CONSTRAINTBAR — per constraint, as AutoCAD's is. */
const CONSTRAINT_BAR =
  "CONSTRAINTBAR — show or hide the markers for the dimensional constraints in this drawing";

export const MODE_OF: Record<string, string> = {
  // ⚠ Express Tools' BREAKLINE. In `MODE_OF` rather than wired by hand so it gets the same
  // `toPlan()` + `arms` treatment as every other drawing tool — a break symbol issued while
  // the camera is orbited has to come back to Top like LINE does.
  "exp.breakline": "breakline",
  // ⚠ MLINE goes through `MODE_OF` like every other draw tool so it gets `toPlan()` and `arms`
  // — a wall drawn while the camera is orbited has to come back to Top the way LINE does.
  "draw.mline": "mline",
  "draw.line": "line",
  "draw.pline": "polyline",
  "draw.circle": "circle",
  "draw.arc": "arc",
  "draw.rect": "rect",
  "draw.hatch": "bucket",
  "draw.xline": "guide",
  // ⚠⚠ **`draw.spline.fit` POINTED AT A MODE THAT DOES NOT EXIST HERE.** `"curve"` was the
  // design app's biarc-polyline tool; pdcad's table has `spline`, which authors a real SPLINE
  // entity through its fit points. The button armed a mode nothing answered to and nothing said
  // so — the failure a name table makes silent.
  "draw.spline.fit": "spline",
  "draw.spline.cv": "splinecv",
  "draw.revcloud": "revcloud",
  "anno.text.single": "text",
  "anno.text.multi": "mtext",
  // ⚠ The button is labelled "Multileader" and carries `cmd: MLEADER`; it armed the mode that
  // draws the old `LEADER`. Typing LEADER still reaches that one — 29.2% of this office's
  // drawings contain one and they have to stay editable.
  "anno.leader": "mleader",
  // ⚠ A two-point drag like RECT, because that is what cutting a window IS. Routing it through
  // `MODE_OF` rather than by hand gets it the same `toPlan()` and `arms` treatment as every
  // other tool that asks for corners.
  "layout.mview": "mview",
  "util.measure.dist": "dist",
  "util.measure.area": "area",
  "block.insert": "furniture",
  // ⚠⚠ **TWELVE VENDOR TOOLS USED TO SIT HERE AND NOT ONE OF THEIR MODES EXISTS IN THIS APP.**
  // `pdvn.wall` armed canvas mode "wall", `pdvn.stair` armed "stair", and so on through door,
  // window, build-up, zone divider, section, detail, set-scale, arrow, paint bucket and rotated
  // rectangle. Measured against `commands.ts` and `Studio.tsx`: not one of those mode strings
  // appears anywhere in this studio. They were live buttons arming nothing, reachable from the
  // PDVN tab and from the `W`/`D`/`N`/`I` keys that tab bound — all removed with it.
  // ⚠ Added the day the plan canvas grew them. PAN, ZOOM Window and POLYGON were grey in both
  // this chrome for a real reason — the canvas genuinely could not do them — and they lit up in
  // the edit that wired them, with no second list to update.
  // That is the whole argument for computing grey rather than declaring it.
  "view.pan": "pan",
  // ⚠ -PAN. Its own mode: `pan` captures the pointer on press, so it never sees a click.
  "view.pan.point": "panpts",
  "view.zoom.window": "zoomwin",
  "draw.polygon": "polygon",
  "clip.paste": "paste",
  // ⚠⚠ ADDED THE DAY THE CANVAS GREW THEM, and two of them were ALREADY REACHABLE FROM THIS
  // MODE'S KEYBOARD before the ribbon admitted they existed: `commands.ts` binds `acad: "A"` to
  // the two new arc gestures and `acad: "UCS"` to the axes tool. A shell whose keyboard can do
  // more than its ribbon shows is the same defect as a button that does nothing.
  //
  // ⚠ `arcctr` is CENTER, START, END — the centre is picked FIRST. It is NOT AutoCAD's
  // "Start, Center, End", which picks the start first, and wiring it to that name would put
  // every arc somewhere the drafter did not ask for. The name has to match the pick order or
  // it stays grey; that is the same rule the other nine arc variants are still held to.
  "draw.arc.cse": "arcctr",
  "draw.arc.3p": "arc3",
  "ucs.set": "axes",
  // ⚠ AutoCAD's POINT and WIPEOUT — real canvas modes as of 2026-08-31, not aliases of
  // something else. POINT places one per click and stays armed; WIPEOUT collects points like a
  // polyline and closes on Enter.
  "draw.point": "point",
  "draw.wipeout": "wipeout",
  // ⚠ DIMCONTINUE and DIMBASELINE take ONE click, off the dimension already on the drawing —
  // so they are their own modes rather than a `dimKind`, which would put them through the
  // two-then-pull gesture the whole point of them is to skip.
  "anno.dim.continue": "dimcont",
  "anno.dim.baseline": "dimbase",
  // ⚠ DIMJOGLINE is the same shape: one click on a dimension that already exists. Listed here
  // so `acadOfMode` can light the button while the mode is armed — a tool whose button does
  // not press looks like a click that missed, and the drafter clicks it again.
  // ⚠⚠ **`anno.dim.jogline` IS NOT ROUTED, AND THAT IS A DECISION WITH A MEASUREMENT BEHIND IT.**
  // AutoCAD's DIMJOGLINE puts a jog symbol on a LINEAR dimension's dimension line, and DXF has
  // no group code for it on a linear dim — `jog_point` (group 15) belongs to `ARC_DIMENSION`
  // alone (checked in ezdxf 1.4.4). AutoCAD carries it as XDATA that only AutoCAD interprets,
  // so building it means authoring bytes this app cannot verify except through the ODA
  // converter. A jog drawn as a plain zig-zag would export as geometry a client's AutoCAD does
  // not recognise as a jog and cannot straighten. Refused with the reason instead — see below.
  "anno.centermark": "centermark",
  // ⚠ `anno.leader.add` is NOT here — see its own entry, and the measurement in it.
  // ⚠⚠ **`anno.leader.remove`, `anno.table`, `draw.measurepts` and `mod.divide` USED TO BE HERE
  // AND EVERY ONE OF THEM ARMED A MODE NOTHING ANSWERED.** Three were greyed anyway, so the
  // entries were dead weight; TABLE's button was LIVE, its note promised "places the door and
  // window schedule as drawn linework", and pressing it did nothing whatever. `qa/command_survey.py`
  // cannot see this — arming a mode lights the button, which is an observable change, so it
  // scored them BUILT. `test/routing.test.ts` walks this table against the modes the studio and
  // the canvas answer, which is what finally named them.
  "mod.stretch": "stretch",
  "anno.centerline": "centerline",
  "anno.dim.quick": "qdim",
  "draw.gradient": "gradient",
  // ⚠ ELLIPSE's three catalogue ids are three PROMPTS in AutoCAD, not three tools — axis-end,
  // centre and arc all make the same entity. Ours takes the axis-end gesture, which is the
  // default and the one the toolbar button runs.
  "draw.ellipse.axis": "ellipse",
  "draw.ellipse.arc": "ellipsearc",
  // ⚠ Still `ellipse` — the GIVEN is what differs, and `ellg` sets it. See `ELLIPSE_GIVENS`.
  "draw.ellipse.center": "ellipse",
  "draw.ellipsegroup": "ellipse",
  "draw.donut": "donut",
  // ⚠ Typed-only in AutoCAD too — see `TYPED_ONLY` in the catalogue. The 2D filled quad is in
  // 36.4% of this office's drawings and has never had a button.
  "draw.solid": "solid",
  // ⚠ Typed-only in AutoCAD too — its options ARE the selection grammar. See `SELECT_MODES`.
  "util.select": "selectobjects",
  "util.setvar": "setvar",
  // ⚠ ELEV asks two questions in a row — see the `elev` mode in `Studio`.
  "util.elev": "elev",
  // ⚠⚠ **A GROUP IS NOT A BLOCK, AND THIS NOTE USED TO SAY IT WAS.** That is true of the app
  // this chrome was lifted from and false here: pdcad stores real `GROUP` objects — 132 of them
  // across 13 of the 90 drawings imported on this box — and they round-trip. A block is ONE
  // object with one insertion point; a group is several objects that stay several, each keeping
  // its own layer, colour and handle.
  "group.create": "group",
  "group.ungroup": "ungroup",
  "group.edit": "groupedit",
  "group.selection": "pickstyle",
  // ⚠ Defining a NEW attribute on a block — the half `ATTEDIT` could not do. `ATTDEF` is in
  // 43.5% of this office's drawings.
  "block.attdef": "attdef",
  // ⚠ ATTSYNC is the OTHER half, and they are separate in AutoCAD for a reason: a sync rewrites
  // every placed copy of the block, and doing that silently as a side effect of defining a
  // field is how a drafter loses the values they typed.
  "block.attsync": "attsync",
  // ⚠ A RAY is not an XLINE with a note attached: it runs ONE way from its base point. The old
  // note here said "the unbounded construction line (XLINE) is built", which was true and was
  // also telling a drafter who wanted a half-line to use the wrong entity.
  "draw.ray": "ray",
  "draw.boundary": "boundary",
};

// ⚠⚠ **EVERY HONOURED SYSTEM VARIABLE IS ALSO A COMMAND**, because in AutoCAD it is: typing
// `LTSCALE` at the prompt asks for a new value directly, without going through SETVAR. Added by
// loop rather than by hand so the list cannot drift from `VAR_CMDS` in the catalogue — a
// variable registered as a command and missing here would press and do nothing.
for (const v of VAR_CMDS) MODE_OF[`util.var.${v.toLowerCase()}`] = `var:${v}`;

/** AutoCAD's visual styles that our 3D view genuinely has, by its own name for each.
 *
 * ⚠ Five of ten. `mono` IS "Shades of Gray" and `xray` IS "X-Ray" — both were invisible while
 * the catalog listed only six styles and omitted those two. The other five are grey with
 * ⚠⚠ Realistic was mapped to `shaded`, which is the style that switches materials OFF —
 * `api.style` computes `const textured = which === "textured"` and calls `applyTexture(m,
 * textured)`, so `shaded` CLEARS the map. AutoCAD's Realistic shows materials, so it is
 * `textured`; AutoCAD's Shaded with Edges is ours, which always draws edges. Six styles, not
 * five: the note that said "ours always shows materials" was describing a viewer setting the
 * AutoCAD shell was never selecting. */
export const VS_OF: Record<string, string> = {
  "vs.wire": "wire",
  "vs.hidden": "hidden",
  "vs.realistic": "textured",
  "vs.shadededges": "shaded",
  // ⚠ AutoCAD's SHADED is shaded WITHOUT the edge overlay — a different style from "Shaded with
  // edges" above, not a synonym. The viewer had no branch that cleared `edges.visible`, which is
  // exactly what its grey note said; now it has one.
  "vs.shaded": "shadedne",
  // ⚠ The two the note called "shader passes it does not have". They did not need a shader —
  // Gooch is a colour ramp off the normal, and Sketchy is the edge geometry with an overshoot.
  "vs.conceptual": "concept",
  "vs.sketchy": "sketchy",
  "vs.gray": "mono",
  "vs.xray": "xray",
};

/** AutoCAD's standard views our camera can actually take. `top` is deliberately absent — Top
 *  view IS the plan canvas, so it is not a camera move, it is a return. */
export const VIEW_OF: Record<string, string> = {
  "view.3d.bottom": "bottom", "view.3d.front": "front", "view.3d.back": "back",
  "view.3d.left": "left", "view.3d.right": "right",
};

/** The reverse — which ribbon button is lit for the studio's current mode.
 *
 * ⚠ FIRST id wins. Two ids can arm the same tool, and lighting the second of them would leave
 * the button the drafter actually pressed looking unpressed. */
export const ACAD_OF_MODE: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  Object.entries(MODE_OF).forEach(([id, mode]) => { if (!out[mode]) out[mode] = id; });
  return out;
})();

/** Which ribbon button is pressed right now.
 *
 * ⚠ The dimension tool is FOUR commands wearing one mode, so the mode alone cannot say which
 * button to light — a drafter who pressed DIMRADIUS would watch DIMALIGNED light up instead. */
export function acadOfMode(mode: string, dimKind?: string,
                           arcGiven?: string, circGiven?: string): string | null {
  if (mode === "dimension") {
    const want = dimKind || "linear";
    const hit = Object.entries(DIM_KIND_OF).find(([, k]) => k === want);
    return hit ? hit[0] : "anno.dim.aligned";
  }
  // ⚠ The arc and circle families are one mode each and eleven buttons, so the LIT one comes
  // from the given, exactly as the dimension family's does from `dimKind`. Without this every
  // entry on the flyout would light at once — which tells the drafter nothing about which
  // gesture is running, and is how you end up clicking three times for the wrong arc.
  if (mode === "arcv") return `draw.arc.${arcGiven || "sce"}`;
  if (mode === "circv") return `draw.circle.${circGiven || "3p"}`;
  return ACAD_OF_MODE[mode] || null;
}

/** The four dimension kinds the plan canvas ALREADY draws, under their AutoCAD names.
 *
 * ⚠⚠ **ALL FOUR WERE BUILT BEFORE THIS SHELL EXISTED AND THREE OF THEM WERE GREY.** `PlanStudio`
 * has had the gestures for months — radius and diameter are centre-then-edge, angular is
 * vertex-then-two-arms — `RadialDimensions.tsx` renders them with each convention observed, and
 * `export._dim_radial` / `_dim_angular` print them. The only way to REACH any of it was a small
 * white card the canvas floats over itself while the dimension tool is active, and this shell
 * had no way to ask, so it greyed them as "not built". That is the same lie as a live button
 * that does nothing, pointing the other way, and it is why `dimKind` is now reported.
 *
 * ⚠ "linear" is the canvas's own word for what AutoCAD calls ALIGNED — `dimShape` draws the line
 * parallel to the two points and labels the true distance.
 *
 * ⚠⚠ **AND "ortholinear" IS AUTOCAD'S DIMLINEAR, WHICH IS A DIFFERENT MEASUREMENT.** It was grey
 * with a note saying exactly that, and the note was right: aligned reads 3026 across a wall that
 * runs 3000 × 400, and 3000 is the number a builder sets out from. DLI is the most-pressed
 * dimension key in AutoCAD, so "we have the other one" was never going to be enough. Built
 * 2026-08-31 by projecting the second point onto the axis the offset pull implies. */
export const DIM_KIND_OF: Record<string, string> = {
  "anno.dim.aligned": "linear",
  "anno.dim.linear": "ortholinear",
  "anno.dim.arc": "arc",
  "anno.dim.angular": "angular",
  "anno.dim.radius": "radius",
  "anno.dim.diameter": "diameter",
  "anno.dim.ordinate": "ordinate",
  "anno.dim.jogged": "jogged",
};

/** Commands that go straight to the server-side edit ops.
 *
 * ⚠⚠ **`test/routing.test.ts` WALKS THIS TABLE.** Every name in here is handed to `Studio`'s
 * `runCmd`, which answers "X is not built yet" for anything it does not recognise — and four
 * commands have shipped that way (BREAKLINE, AREA/DIST/BREAK, PAN, EXPLODE), each with a working
 * server half and a live-looking button. Grey cannot catch it: the handler exists. ⚠ The test
 * reads this file as TEXT rather than importing it — `handlers.ts` pulls in `.tsx`, which the
 * node test runner cannot load — so keep the entries one per line and quoted. */
export const OP_OF: Record<string, [string, any?]> = {
  "mod.move": ["move"],
  "mod.copy": ["copy"],
  "mod.rotate": ["rotate"],
  "mod.scale": ["scale"],
  "mod.offset": ["offset"],
  "mod.array.rect": ["array"],
  // ⚠⚠ **POLAR ARRAY IS BUILT AND ITS BUTTON WAS DEAD.** This read `["polar_array"]`; there is
  // no such op on the server and `Studio.runCmd` has no such name, so the ribbon's Polar Array
  // answered "POLAR_ARRAY is not built yet" — while the polar dialog it wanted sat two lines
  // away in the SAME handler the rectangular button uses (`setArrayDlg(extra.kind === "polar"
  // ? "polar" : "rect")`). Found by `test/routing.test.ts`, not by anyone using it.
  "mod.array.polar": ["array", { kind: "polar" }],
  "mod.fillet": ["fillet"],
  "mod.chamfer": ["chamfer"],
  "mod.explode": ["explode"],
  // ⚠ `join` and `reverse` moved down to the PEDIT block — the ops are `pjoin` and
  // `pedit {mode: reverse}`. They were declared here AND there, and a duplicate key in an
  // object literal is silently won by the last one: the pair would have kept working while
  // reading as if these lines were live.
  // ⚠⚠ **UNION, SUBTRACT, INTERSECT AND DIVIDE CALLED SERVER OPS THIS BACKEND DOES NOT HAVE.**
  // They dispatched `boolean` and `divide`; `edits.OPS` has neither, so every one of them was a
  // live button whose only possible outcome was a 400. They are grey now, with a reason.
  //
  // ⚠⚠⚠ **AND FOUR MORE WERE SITTING RIGHT UNDER THAT PARAGRAPH DOING THE SAME THING.** ALIGN,
  // OVERKILL, LENGTHEN and BLEND dispatched `align`, `overkill`, `lengthen` and `blend`; the
  // backend has none of the four — `grep 'op == "overkill"' edits.py` finds nothing — so each
  // was a live button whose only possible outcome was "OVERKILL is not built yet". The note
  // that used to sit here read *"OVERKILL and LENGTHEN are ordinary selection ops — pick
  // objects, press the button"*, which describes how they WOULD be wired if they existed.
  // They are grey now, with a reason, beside UNION's — see `WHY` and the entries under
  // `mod.union` in the handler record. Found 2026-09-09 by
  // `test/routing.test.ts`; see `docs/KERNEL_GAPS.md` for what building them would take.
  // ⚠ STRETCH is not in this table either, but for a different and correct reason: it is wired
  // as a MODE below, because it needs a window and a displacement, which is a gesture.

  // ⚠⚠ **SETBYLAYER NEEDS NO NEW OP — `setprop` ALREADY WRITES THESE THREE.** It dispatched a
  // `setbylayer` op that does not exist. What the command means is exactly a `setprop` of
  // colour 256 (ByLayer), linetype BYLAYER and lineweight -1 (ByLayer) onto the selection, and
  // `do_setprop` writes all three today, tags and columns together, with a real undo.
  "mod.setbylayer": ["setprop", { aci: 256, linetype: "BYLAYER", lineweight: -1 }],
  // ⚠ OVERKILL takes NO selection when none is made: with no handles the server judges the whole
  // drawing, which is what a drafter running it on a file from a consultant means. See
  // `edits.do_overkill` — it is deliberately absent from `NEEDS_SELECTION`.
  "mod.overkill": ["overkill", {}],
  // ⚠ BLEND is in `PICKS`, so the ribbon button ARMS it and the two clicks answer it — the same
  // route FILLET takes. Nothing here needs the picks; `armPick` collects them.
  "mod.blend": ["blend", {}],
  // ⚠ PEDIT's four options are four entries, not one command with an argument the command line
  // cannot ask for. Each is its own catalog id on the button's flyout — same shape as POINT's
  // PDMODE list, and for the same reason.
  "mod.pedit.close": ["pedit", { mode: "close" }],
  "mod.pedit.width": ["pedit", { mode: "width" }],
  "mod.pedit.addvertex": ["pedit", { mode: "addvertex" }],
  "mod.pedit.delvertex": ["pedit", { mode: "delvertex" }],
  "mod.pedit.arc": ["pedit", { mode: "arc" }],
  "mod.pedit.straight": ["pedit", { mode: "straight" }],
  // ⚠ JOIN and REVERSE already had ids in the flyout pointing at the standalone commands —
  // see the catalog note. They route to the real ops now rather than to nothing.
  "mod.join": ["pjoin", {}],
  "mod.reverse": ["pedit", { mode: "reverse" }],
  "mod.pedit.open": ["pedit", { mode: "open" }],
  "mod.pedit.fit": ["pedit", { mode: "fit" }],
  "mod.pedit.decurve": ["pedit", { mode: "decurve" }],
};

/** Commands that ask for a point (or an axis) after the selection — AutoCAD's own flow.
 *
 * ⚠ These four are the ones where our canvas ALREADY prompts for a pick, and the prompt is the
 * same one AutoCAD prints: TRIM wants the piece to cut away, MIRROR wants the two points of the
 * mirror line. Wiring them through `runCmd` instead would have thrown the pick away and used a
 * typed number, which is not what any of the four means. */
const PICK_OF: Record<string, ["trim" | "extend" | "break" | "mirror", "point" | "axis"]> = {
  "mod.trim": ["trim", "point"],
  "mod.extend": ["extend", "point"],
  // ⚠ `mod.break` is NOT here any more — see its own entry. `PICK_OF` arms a canvas mode that
  // takes ONE object and ONE point, and BREAK takes one object and TWO.
  "mod.mirror": ["mirror", "axis"],
};

export interface AcadContext {
  mode: string;
  setMode: (m: string) => void;
  /** `st.actions` off the canvas. */
  A: any;
  /** `st.tool` off the canvas. */
  T: any;
  /** The whole reported canvas state. */
  st: any;
  plan: any;
  planId: number;
  nav: (to: string) => void;
  run: (key: string, fn: () => Promise<any>, msg?: string) => void;
  /** Say something the user needs to read. */
  warn: (s: string) => void;
  /** Open (or close) a docked palette by id. */
  showPalette: (id: string, on?: boolean) => void;
  /** Whether a palette is docked right now, for the tick in Tools ▸ Palettes. */
  paletteOpen: (id: string) => boolean;
  /** Open the studio's command palette — which is also where the interface is chosen. */
  options: () => void;
  exportAs: (fmt: "pdf" | "dxf" | "dwg" | "dae" | "obj" | "ifc" | "stl"
                  | "slice") => Promise<void>;
  print: () => void;
  /** Open the Plot dialog on this drawing, or on the composed set. */
  openPlot: (what: "drawing" | "set") => void;
  /** Plot straight away with the remembered settings — PREVIEW skips the dialog, as AutoCAD's
   *  ribbon Preview does. `preview` opens the result in a tab instead of downloading it. */
  plotNow: (what: "drawing" | "set", preview: boolean) => void;
  /** How many composed sheets the drawing has; null until the answer arrives. */
  sheets: number | null;
  /** null = model space; a page id = that layout. */
  space: number | null;
  setSpace: (v: number | null) => void;
  /** The drawing's composed pages, which ARE its layout tabs. */
  pages: { id: number; name: string }[];
  /** Open the View Manager — AutoCAD's VIEW. */
  openViews: () => void;
  /** TESTDRIVE — hand the drafter a scripted run and time it. See `AcadTestDrive`. */
  openTestDrive: () => void;
  /** Open Quick Select — AutoCAD's QSELECT. */
  openQSelect: () => void;
  /** Open Find — AutoCAD's FIND. */
  openFind: () => void;
  /** "plan" = the drawing canvas (AutoCAD's Top view); anything else = the 3D viewport. */
  camera: string;
  setCamera: (v: string) => void;
  /** Run something against the 3D view, entering it (and queueing) if it is not up yet. */
  go3d: (fn?: (a: any) => void, cam?: string) => void;
  /** `Model3D`'s reported `{tool, actions}`, or null while the camera is on the plan. */
  m3d: any;
  /** The drawing's layers, as the server last reported them. */
  layers: any[];
  /** Write `visible`/`locked`/`printable` to named layers and re-read the drawing. Returns how
   *  many layers were touched, so a command can say "3 layers off" instead of nothing. */
  patchLayers: (names: string[], patch: any, remember?: boolean) => number;
  /** LAYERP — put the layers back one step. */
  layerPrev: () => boolean;
  canLayerPrev: boolean;
  /** Open the one layer dialog, in one of its five modes. */
  openLayerDlg: (d: any) => void;
  /** Open the DWG/DXF import dialog — AutoCAD's INSERT ▸ Import and XATTACH. */
  openImport: () => void;
  /** INTERFERE — report the pieces of this model that occupy the same space. */
  interfere: () => void;
  /** PDFIMPORT — open the dialog that turns a PDF's vectors into geometry. */
  openPdfImport: () => void;
  /** Open the Solid tab's body dialog on one kind — "" while it is shut. */
  makeBody: (kind: string) => void;
  bodyKind: string;
  /** ATTDEF/BATTMAN open "defs"; EATTEDIT opens "values". "" while the window is shut. */
  openAttrs: (tab: "defs" | "values") => void;
  attrTab: "" | "defs" | "values";
  /** BLOCK makes one from the selection and replaces it; WBLOCK keeps the geometry. */
  makeBlock: (toLibrary: boolean) => void;
  /** SHELL / SLICE / IMPRINT — the ops that act on a solid's faces. */
  solidFaceOp: (op: "shell" | "slice" | "imprint" | "offsetedge") => void;
  /** CONVTOSOLID — an imported triangle mesh sewn into a B-rep so it can be booleaned. */
  meshToSolid: () => void;
  /** One of the Surface tab's skins, built from the selected curve(s). */
  makeSurface: (kind: string) => void;
  /** POINTLIGHT / SPOTLIGHT / DISTANTLIGHT. */
  placeLight: (kind: "point" | "spot" | "distant") => void;
  lightsOff: () => void;
  /** UCSMAN — the named-origin manager. */
  ucsManager: () => void;
  delSurfaces: () => void;
  /** BEDIT — open the selected made block's definition on the drawing. */
  beditOpen: () => void;
  /** Save (`true`) or discard (`false`) the open definition. */
  beditClose: (save: boolean) => void;
  /** The block currently open for editing, or `null`. */
  bedit: { block: number; name: string; code: string } | null;
  /** The Parameters Manager, and the dimensional constraints it drives. */
  openParams: () => void;
  paramsOpen: boolean;
  addConstraint: (kind: string) => void;
  constraintBar: (show: boolean) => void;
  /** DELCONSTRAINT — the selection's constraints, or every constraint when nothing is picked. */
  delConstraints: () => void;
  /** DIMBREAK — gap the drawing's dimension lines at crossings, or clear the gaps. */
  dimBreak: (remove: boolean) => void;
  /** The 3D solid booleans — `DesignSolidOp`, stored as the operation. */
  solids3d: (op: string) => void;
  /** VPORTS: "" is one viewport, "2v" is the drawing beside a live 3D view. */
  vports: string;
  setVports: (v: string) => void;
  /** What VPORTS ▸ Restore puts back. */
  lastVports: string;
  /** Re-read the drawing after a server-side write. */
  reload: () => Promise<any>;
  /** XCLIP — write the underlay's clip rectangle to the plan, or null to remove it. */
  setUnderlayClip: (r: number[] | null) => void;
  /** The active workspace id — a key of `WS_TABS`. */
  ws: string;
  /** Change remembered chrome settings. Used by the workspace switch, which has to move the
   *  ribbon's tab set and its open tab together. */
  setCfg: (patch: { ws?: string; tab?: string }) => void;
}

export function buildAcadHandlers(c: AcadContext): AcadHandlers {
  const { A, T, st } = c;
  const has = !!st?.selected;
  const snaps: string[] = T?.snaps || [];
  const clipCount: number = T?.clipCount || 0;
  /** Is the 3D viewport the thing on screen? */
  const cam3d = c.camera !== "plan";

  /** ⚠⚠ **A DRAWING COMMAND ISSUED WHILE ORBITED RETURNS TO TOP VIEW — IT DOES NOT GREY.**
   *  The plan canvas is not mounted while the 3D viewport owns the screen, so LINE genuinely
   *  cannot run there. The obvious answer is to grey every drawing command whenever the camera
   *  moves, and it is the wrong one twice over: it makes grey mean "not right now" as well as
   *  "not built" — the distinction this whole mode rests on — and it makes a drafter undo their
   *  own view before they can draw. Pressing LINE means "I want to draw a line", so it goes
   *  back to Top view and arms the tool, and says so. */
  const toPlan = () => {
    if (c.space !== null) { c.setSpace(null); c.warn("Returned to Model space to draw."); }
    else if (c.camera !== "plan") { c.setCamera("plan"); c.warn("Returned to Top view to draw."); }
  };

  /** A drawing tool. ⚠ `arms` hands the keyboard back to the canvas — see `AcadHandler`. */
  const tool = (id: string, note?: string): AcadHandler => ({
    run: () => { toPlan(); c.setMode(MODE_OF[id]); }, arms: true, ...(note ? { note } : {}),
  });

  /** A dimension: one mode, four kinds. Sets both, so the gesture matches the name pressed. */
  const dim = (id: string, note: string): AcadHandler => ({
    run: () => { toPlan(); c.setMode("dimension"); A.setDimKind?.(DIM_KIND_OF[id]); },
    arms: true, note,
  });

  /** An arc or a circle from AutoCAD's flyout: one mode, eleven givens. Same shape as `dim` —
   *  the name pressed IS the gesture, so both the mode and the given are set together. */
  const arcg = (g: string, note: string): AcadHandler => ({
    run: () => { toPlan(); A.setArcGiven?.(g); }, arms: true, note,
  });
  const circg = (g: string, note: string): AcadHandler => ({
    run: () => { toPlan(); A.setCircGiven?.(g); }, arms: true, note,
  });
  /** ⚠ ELLIPSE's two givens, same shape. `Axis, End` must set `axis` EXPLICITLY rather than
   *  leave whatever was chosen last standing — the two menu entries are a pair, and one that
   *  only ever switches one way would make `Center` permanent. */
  const ellg = (g: string, note: string): AcadHandler => ({
    run: () => { toPlan(); A.setEllGiven?.(g); }, arms: true, note,
  });

  // ⚠⚠ NOT GREYED ON AN EMPTY SELECTION. AutoCAD does not grey MOVE because nothing is picked —
  // you press it and it answers "Select objects:". Greying here would make grey mean two things
  // at once ("we have not built this" and "you have not clicked anything"), which is the mistake
  // the SketchUp lane made first and had to undo. Grey means only the first; an empty selection
  // is told so in words, in the command line, where AutoCAD would say it.
  // ⚠⚠ **`BOOL_3D` LIVED HERE AND IT DESCRIBED A PRODUCT THIS IS NOT.** It dispatched UNION,
  // SUBTRACT and INTERSECT "by VIEWPORT" — flat shapes on the drawing canvas, real solids
  // through OCCT when the 3D view was in front. There is no 3D view in pdcad (`package.json`
  // has no three.js, the canvas is a 2D context) and no solid modeller, so the whole dispatch
  // resolved to a branch that could never be taken and a note describing walls and joinery to
  // someone who had opened a DWG.
  /** ⚠⚠ **COMMANDS THAT PICK THEIR OWN OBJECTS AFTER THEY START, SO "SELECT FIRST" IS WRONG.**
   *  AutoCAD's FILLET and CHAMFER ask for a radius and then for two objects — there is nothing
   *  to select beforehand. The guard below is right for MOVE and refused these two outright:
   *  `_FILLET` printed on the command line, the handler returned, and the drafter's next two
   *  clicks selected the walls instead of filleting them. */
  // ⚠ OFFSET joined them on 2026-09-09: it asks for a DISTANCE and then for one object at a
  // time, so there is nothing to select beforehand and a `Select objects:` prompt would be a
  // question AutoCAD does not ask.
  const PICKS_OWN = new Set(["mod.fillet", "mod.chamfer", "mod.offset"]);
  const op = (id: string): AcadHandler => ({
    run: () => {
      toPlan();
      const [name, extra] = OP_OF[id];
      // ⚠⚠ **VERB FIRST — AutoCAD ASKS, IT DOES NOT REFUSE.** Press MOVE with nothing picked
      // and AutoCAD answers `Select objects:`; this printed "Select objects first." and threw
      // the command away. That is half of how AutoCAD is driven — the older habit, and still
      // the commoner one — refused outright. `selectThen` starts the SELECT prompt and runs the
      // command when the drafter presses ENTER, with the whole select grammar (ALL, Previous,
      // Last, Fence, WPolygon, CPolygon) available because it is the same state.
      // ⚠ `PICKS_OWN` still short-circuits: FILLET and CHAMFER ask for a radius and then for
      // two objects, so there is nothing to select beforehand and a prompt would be wrong.
      if (!has && !PICKS_OWN.has(id)) { A.selectThen?.(name, extra); return; }
      A.runCmd?.(name, extra);
    },
    // ⚠⚠ **`arms` BECAUSE EVERY ONE OF THESE THEN ASKS THE DRAWING A QUESTION**, and the answer
    // is typed. Without it the command line keeps focus, and `M ⏎` followed by `0,0 ⏎` puts the
    // coordinate in the command line, where it resolves as a command name and comes back as
    // *Unknown command "0,0"*. It is the same defect the LINE/PLINE handlers fixed by arming,
    // and it was latent on the whole MODIFY panel: `Select objects:` answered `ALL` with
    // *Unknown command "ALL"*, because the select grammar's words are answers to a PROMPT and
    // the box that had focus reads command names.
    arms: true,
  });
  const pick = (id: string): AcadHandler => ({
    run: () => {
      toPlan();
      const [name, needs] = PICK_OF[id];
      // `startPick` refuses (and says so) when nothing is selected, in the studio's own voice.
      A.startPick?.(name, needs);
    },
  });

  /** A palette that is a frame round a panel the studio already ships. */
  const pal = (id: string): AcadHandler => ({
    run: () => c.showPalette(id), on: c.paletteOpen(id),
  });

  // ⚠ Built once and spread, so the two that also want a NOTE can re-use their own run/on
  // rather than restating them — a second copy of `a.style(v)` is a second thing to keep in step.
  const vsHandlers: AcadHandlers = Object.fromEntries(Object.entries(VS_OF).map(([id, v]) => [id, {
    run: () => c.go3d((a) => a.style(v)),
    on: cam3d && c.m3d?.tool?.style === v,
  }]));
  const ops: AcadHandlers = Object.fromEntries(Object.keys(OP_OF).map((id) => [id, op(id)]));
  const picks: AcadHandlers = Object.fromEntries(Object.keys(PICK_OF).map((id) => [id, pick(id)]));
  const tools: AcadHandlers = Object.fromEntries(Object.keys(MODE_OF).map((id) => [id, tool(id)]));

  return {
    ...tools,
    ...ops,
    ...picks,

    // ── File, and the Application menu ───────────────────────────────────────────────────
    "file.new": { note: "every drawing here starts from an imported DWG or DXF — File ▸ Open lists what is in the database, and Import brings a new one in" },
    // ⚠⚠ **THIS WENT TO `/design/plans` AND THEREFORE SIGNED THE DRAFTER OUT** — see `nav` in
    // `Studio.tsx`. pdcad HAS an Open dialog, on Ctrl+O, and `A.open` is what opens it.
    "file.open": { run: () => A.open?.() },
    "file.save": { run: () => A.save?.() },
    // ⚠⚠ AutoCAD's CLOSE closes the drawing and QUIT leaves the application. This pointed at
    // "the drawing's own data sheet", which is a screen in the DESIGN app — so it signed the
    // drafter out instead. There is no close-without-exit here yet: the studio always has a
    // drawing or the empty state, and nothing steps back to the empty state on purpose.
    "file.close": { note: "closes the drawing without leaving the app. Not built — File ▸ Open swaps drawings directly, and Exit signs out" },
    // ⚠ In the old app EXIT went back to a drawings list; pdcad's studio IS the whole app, so
    // the honest meaning of Exit here is signing out. The note says so — a menu item whose
    // wording describes the other product is how a drafter learns not to trust the menu.
    "file.exit": { run: () => c.nav("/signout"), note: "signs you out of pdcad" },
    "file.export.dxf": { run: () => { void c.exportAs("dxf"); } },
    // ⚠ AutoCAD's own placement: the NATIVE format is SAVEAS, not Export — Export is for the
    // formats that are somebody else's. So there is deliberately no `file.export.dwg`;
    // `file.saveas` above is the DWG route, which is where a drafter's hand already goes.
    "out.exportpdf": { run: () => { void c.exportAs("pdf"); } },
    // ⚠⚠ **SAVE AS STAYS GREY, AND I ALMOST WIRED IT.** AutoCAD's Save As produces a .dwg and
    // `/export/?fmt=dwg` looks like it produces one, so it went on the list of "built but
    // unreachable" with the other four. It is not. The server answers **501**: *"DWG needs the
    // ODA File Converter, which is not installed on this server."* Measured today against plan
    // 400 through the real endpoint: dxf 200 · **dwg 501** · ifc 200 · dae 200 · obj 200 ·
    // slice 200. A download that errors is worse than a button that never offered it — the note
    // that was already here said exactly that, and checking it is what stopped this becoming a
    // regression dressed as a feature. The reason now names the BLOCKER rather than the database.
    // ⚠⚠ **THIS NOTE WAS OUT OF DATE AND IT WAS THE LOAD-BEARING KIND.** It said the DWG
    // writer needs the ODA File Converter and the server answers 501. LibreDWG is built on this
    // box and writes R2000, and the server now writes, RE-READS and counts one before it will
    // hand it over — so the honest note is about the remaining gap, not about a missing tool.
    "file.saveas": {
      run: () => { void c.exportAs("dwg"); },
      note: "saves an R2000 .dwg — written by LibreDWG, then read back and counted here before "
            + "you get it. A version that would lose a drawn object is refused with the "
            + "numbers. ⚠ No AutoCAD on this machine has opened one yet, so check the first one "
            + "you send",
    },
    // ── the deliverables this office actually sends ─────────────────────────────────────
    ...Object.fromEntries(([
      ["ifc", "ifc", "openBIM — what a main contractor's model coordinator opens. Walls, "
                     + "openings, slabs and fitments as IFC entities, not as lines"],
      ["dae", "dae", "Collada mesh, for a renderer or a game engine"],
      ["obj", "obj", "Wavefront mesh — the most widely readable of the three"],
      ["slice", "slice", "the current cut plane as a flat DXF, for a consultant to trace over. "
                         + "Saved as .dxf, because a file called .slice opens in nothing"],
    ] as [string, any, string][]).map(([id, fmt, note]) => [`out.${id}`, {
      run: () => { void c.exportAs(fmt); },
      note,
    } as AcadHandler])),
    // ⚠ PLOT opens the DIALOG, it does not print. AutoCAD's Ctrl+P has never fired a plot
    // straight at the paper, and a button that silently produced a PDF at whatever settings it
    // last used is the one thing worse than a grey one — it wastes a sheet before you can look.
    "out.plot": { run: () => c.openPlot("drawing"),
                  note: "paper size, orientation, scale and what the sheet shows" },
    // AutoCAD's ribbon Preview goes straight to the preview, skipping the dialog. So does ours,
    // at the settings the dialog remembers.
    "out.preview": { run: () => c.plotNow("drawing", true),
                     note: "plots at the remembered settings and opens it, without the dialog" },
    // PUBLISH is the composed set — `to_sheets`, the same deliverable the studio's Print makes.
    "out.batchplot": { run: () => c.openPlot("set") },
    "file.publish": { run: () => c.openPlot("set") },
    // ⚠ PAGESETUP genuinely edits the same three things — paper, scale, orientation — so it
    // opens the same dialog rather than a second one that would drift. What is NOT built is the
    // NAMED page setup, and the dialog's greyed Name row says exactly that.
    "out.pagesetup": { run: () => c.openPlot("drawing"),
                       note: "the paper, scale and orientation live in the Plot dialog" },
    // ⚠⚠ BOTH POINTED AT `?tab=qty` — the design app's quantity takeoff, computed from a
    // building model. pdcad has no model to take quantities off: it has entities read from a
    // DWG, and counting them is not a schedule.
    "insert.dataextract": { note: "extracts block attributes into a table or a spreadsheet. The attributes ARE read and editable (see ATTEDIT and Manage Attributes); nothing gathers them across a drawing yet" },
    "anno.table.extract": { note: WHY["anno.table.link"] },

    // ── Edit ────────────────────────────────────────────────────────────────────────────
    "edit.undo": { run: () => A.undo?.() },
    "edit.redo": { run: () => A.redo?.() },
    "edit.cancel": { run: () => c.setMode("select"), arms: true },
    // ⚠ ERASE is not an `op` — it commits straight away rather than asking for points — so it
    // needed the verb-first branch spelling out. `["erase"]` is the studio's own command name,
    // and `A.remove` is what `runCmd("erase")` reaches; routing the deferred case through the
    // same name keeps one definition of what ERASE does.
    "mod.erase": {
      run: () => {
        if (!has) { A.selectThen?.("erase"); return; }
        A.remove?.();
      },
      arms: true,
    },
    // ⚠⚠ **THE ACTION HAS BEEN ON `PlanStudio` ALL ALONG.** This was grey behind "nothing
    // selects the whole drawing at once yet — a crossing marquee does the same job", and the
    // marquee does NOT do the same job: it takes what it crosses, so anything scrolled off
    // screen is missed. `actions.selectAll` selects shapes, walls AND blocks, skipping hidden,
    // locked and locked-layer rows — which is AutoCAD's own rule, because a Select All that
    // sweeps in something the drafter cannot see gets deleted by the next Delete.
    "edit.selectall": {
      run: () => { toPlan(); A.selectAll?.(); },
      note: "selects every visible, unlocked object on the drawing. ⚠ Hidden and locked ones "
          + "are skipped — a selection you cannot see is one the next Delete removes",
    },

    // ⚠⚠ **A REAL CLIPBOARD NOW, AND PASTE IS THE ONE PLACE THIS SHELL USES `enabled`.**
    // Everywhere else grey means exactly one thing — "not built" — because a grey that ALSO
    // means "not right now" turns every grey button into a question the user has to hover to
    // answer. Paste is the exception, under a rule that holds generally: **does the grey teach
    // the user something true about the COMMAND, or only about this INSTANT?** Move with nothing
    // selected is only about the instant — Move works, you simply have not picked anything — so
    // it stays live and says so in words. Paste with an empty clipboard has no possible outcome
    // at all; that is a fact about the command, every application greys it, and nobody has ever
    // been confused by a greyed Paste.
    "clip.cut": { run: () => A.clipCut?.(),
                  note: "cuts the selected SHAPES; walls, openings and furniture are not copied" },
    "clip.copy": { run: () => A.clipCopy?.(),
                   note: "copies the selected SHAPES — walls, openings and furniture are not" },
    "clip.paste": {
      run: () => c.setMode("paste"), arms: true,
      enabled: clipCount > 0,
      note: clipCount ? "click where the clipboard's bottom-left corner should land"
                      : "the clipboard is empty",
    },
    "clip.pasteorig": {
      run: () => A.clipPasteOrig?.(),
      enabled: clipCount > 0,
      note: clipCount ? "pastes back at the coordinates it was copied from"
                      : "the clipboard is empty",
    },
    // ⚠⚠ **THE BOUNDING-BOX CORNER IS ALMOST NEVER THE POINT YOU PLACE BY.** For a door leaf it
    // is a corner of empty air; what a drafter aligns by is the hinge, the centreline, a grid
    // intersection. That is the whole of COPYBASE, and it was grey with its own reason written
    // out correctly — "choosing your own is not built" — which is a specification, not an
    // excuse.
    "clip.copybase": {
      run: () => { toPlan(); A.clipCopyBase?.(); },
      note: "copies the selection, then asks you to pick the point it will paste BY — the "
          + "hinge, a centreline, a grid intersection. ⚠ Plain Copy uses the bounding box's "
          + "bottom-left corner and clears any base you picked before",
    },
    // ⚠ PASTESPEC has exactly ONE honest meaning here. AutoCAD offers a list of clipboard
    // FORMATS and this app has one format — so the choice that matters is whether the geometry
    // keeps the layer it came from or joins the one you are drawing on. Pasting a detail copied
    // from A-DIMS back onto A-DIMS while you are working on A-FURN is the same mistake.
    "clip.pastespec": {
      run: () => A.clipPasteSpec?.(), enabled: clipCount > 0,
      note: clipCount ? "pastes onto the layer you are DRAWING on, instead of the one each "
                      + "object came from"
                      : NO_CLIP,
    },
    "clip.pasteblock": {
      run: () => { A.clipPasteBlock?.(); },
      enabled: clipCount > 0,
      // ⚠ ONE server op, not paste-then-block. Pasting and then blocking would put the loose
      // objects in the drawing for a moment and leave them there if the second call failed.
      note: clipCount ? "pastes the clipboard as ONE block — pick where it goes. The block is "
                      + "anonymous (*U…), so it stays out of the Insert list, like AutoCAD's"
                      : NO_CLIP,
    },

    // ── Draw ────────────────────────────────────────────────────────────────────────────
    // ⚠ The tools above are wired by `MODE_OF`. What follows is everything AutoCAD draws that
    // we do not, and the two commands whose GESTURE differs from the one tool we have.
    "draw.hatch": { ...tool("draw.hatch"),
                    note: "pours the drawing's current MATERIAL into the region, not a pattern" },
    // ⚠⚠ **THREE OF AutoCAD'S FIVE CIRCLES, AND THE TWO THAT ARE LEFT NEED SOMETHING ELSE.**
    // Centre/Diameter, 2-Point and 3-Point are arithmetic on the picks and nothing more, which
    // is why they had been grey for no better reason than that nobody had written them.
    // ⚠⚠ **`Center` DREW AN AXIS-END ELLIPSE UNTIL 2026-09-07** — the id mapped to the same mode
    // as `Axis, End` and there was no given to distinguish them, so the click meant as the middle
    // was read as a rim point. Both entries set the given now, and `Axis, End` sets it back.
    "draw.ellipse.center": ellg("c", "click the CENTRE, then one axis end, then the other radius"),
    "draw.ellipse.axis": ellg("axis", "the two ends of ONE axis, then a point whose distance "
                                      + "gives the other radius"),
    "draw.circle.cd": circg("cd", "click the centre, then type the DIAMETER or click the rim"),
    "draw.circle.2p": circg("2p", "the two clicks are the ends of a DIAMETER, not centre and rim"),
    "draw.circle.3p": circg("3p", "three points on the rim — the circle through all of them. "
                                  + "Three in a line have none, and it keeps asking"),
    // ⚠⚠ **THE OLD NOTE WAS EXACTLY RIGHT ABOUT WHAT WAS MISSING** — "needs the tangency solve,
    // not a third point" — so the answer was to write the tangency solve rather than to reword
    // it. These two pick OBJECTS, which none of the other five circle gestures do.
    //
    // ⚠ It is closed form, not an iteration. The locus of centres of a circle of radius r
    // tangent to a line is two parallels at ±r; tangent to a circle of radius R, two concentric
    // circles of |R ± r|. Intersect two loci and every answer falls out — up to four of them,
    // and the one nearest the cursor wins, which is how AutoCAD disambiguates too.
    // ⚠⚠ **NOUN-VERB, WHICH IS A REAL DIFFERENCE FROM AutoCAD AND THE TOOLTIP SAYS SO.**
    // AutoCAD prompts "Select first object:"; pdcad selects first and runs second everywhere
    // else, so these take their objects from the selection. The click that follows is the
    // disambiguation AutoCAD gets from where on each object you picked.
    "draw.circle.ttr": circg("ttr", "SELECT two lines, arcs or circles first, then press this: "
                                    + "it asks for the radius, and one click says which of the "
                                    + "circles you meant. ⚠ Up to four fit"),
    "draw.circle.ttt": circg("ttt", "SELECT three straight lines first, then press this and "
                                    + "click near the one you want — the incircle and the three "
                                    + "excircles all touch all three. ⚠ Three LINES: with a "
                                    + "circle among them this is the Apollonius problem, and it "
                                    + "refuses rather than guessing"),
    // ⚠⚠ EVERY NAMED ARC VARIANT IS GREY WHILE PLAIN ARC IS LIVE, and that is the point rather
    // than an oversight. Ours is start → end → a point the arc passes through. Each of these
    // eleven names PROMISES a different pick order; a drafter reaching for "Start, Center, End"
    // and getting our gesture would put the arc somewhere else and find out later.
    // ⚠⚠ TWO OF THE ELEVEN CAME TRUE on 2026-08-31 and the rule did not move an inch: the
    // canvas grew `arc3` (a point, a point it passes through, a point) and `arcctr` (centre,
    // start, end), so **3-Point** and **Center, Start, End** now name gestures we actually
    // have and they are live. The other nine are still grey for exactly the reason they always
    // were. Note in particular that `arcctr` lights **cse** and NOT **sce**: "Start, Center,
    // End" picks the start first, ours picks the centre first, and the two produce different
    // arcs from the same three clicks.
    // ⚠⚠ **AND NOW ALL NINE ARE BUILT** (2026-09-01). The blanket note that used to sit here
    // said the canvas had three pick orders — true, and it was hiding that the other nine are
    // the SAME entity from different givens, not nine new tools. `solveArc` is property-tested
    // in `geometry.ts`; these only name the question each one asks.
    // ⚠ Six of them ANSWER WITH A NUMBER, so every note says which box to type it in. A drafter
    // who reads "Start, Centre, Angle" and finds no way to enter an angle has been given a
    // worse version of the plain ARC tool.
    "draw.arc.sce": arcg("sce", "start, then the centre (which fixes the radius), then a point "
                              + "on the closing ray — counterclockwise, so it can pass 180°"),
    "draw.arc.sca": arcg("sca", "start, centre, then the included ANGLE in the command bar's "
                              + "Angle box. Positive is counterclockwise; 270 is a valid arc"),
    "draw.arc.scl": arcg("scl", "start, centre, then the CHORD length in the Distance box — a "
                              + "chord longer than the diameter has no arc"),
    "draw.arc.sea": arcg("sea", "both ends, then the included ANGLE in the Angle box"),
    "draw.arc.sed": arcg("sed", "both ends, then a point giving the TANGENT direction at the "
                              + "start — the one of these that is a direction, not a number"),
    "draw.arc.ser": arcg("ser", "both ends, then the RADIUS in the Distance box; a negative "
                              + "radius takes the major arc. Under half the chord it cannot reach"),
    "draw.arc.csa": arcg("csa", "centre first, then the start, then the included ANGLE — the "
                              + "same arc as Start, Centre, Angle from the other pick order"),
    "draw.arc.csl": arcg("csl", "centre first, then the start, then the CHORD length"),
    // ⚠ The only one that can be genuinely unavailable: it continues the last line or arc, and
    // before anything has been drawn there is nothing to continue from. That is a STATE grey,
    // like Paste with an empty clipboard, and the note says so rather than claiming it is unbuilt.
    "draw.arc.cont": T?.canContinueArc
      ? arcg("cont", "picks up where the last arc or line ended, tangent to it — one click for "
                   + "the end, which is what AutoCAD's Enter at the Start prompt does")
      : { note: "continues the last arc or line, tangent to it — nothing has been drawn yet" },
    "draw.arc.3p": { ...tool("draw.arc.3p"),
                     note: "a point on the arc, a point it passes through, then the last point" },
    "draw.arc.cse": { ...tool("draw.arc.cse"),
                      note: "centre first, then the start (which sets the radius), then the end "
                            + "— up to a half turn" },
    "draw.arc": { ...tool("draw.arc"),
                  note: "start, end, then a point the arc passes through" },
    // ⚠ The side count is reported by the canvas, so the tooltip states the number this press
    // will actually draw rather than a generic "polygon". AutoCAD prints the same thing in its
    // own prompt, as `<6>`.
    "draw.polygon": { ...tool("draw.polygon"),
                      note: "centre, then a vertex. The side count is ASKED on the command line, the way AutoCAD asks it" },
    "draw.region": { note: "flat shapes here already behave as regions — see Modify ▸ Solid Editing" },

    // ⚠ Still not built. They were grey with `IN_3D` — "open it in the 3D view" — which stopped
    // being true the moment the 3D view arrived HERE: the note now pointed at a place that has
    // no 3D polyline either. A stale where-to-find-it is worse than a plain "not built yet",
    // because it sends someone looking.
    "draw.3dpoly": { note: WHY["draw.3dpoly"] },
    "draw.helix": { note: WHY["draw.helix"] },
    // ⚠ POINT and DDPTYPE. `MODE_OF` arms the tool; the style picker is a drop-up on the
    // button's own flyout rather than AutoCAD's modal dialog, because the dialog's whole
    // content is one grid of ten markers.
    ...Object.fromEntries(PDMODES.map((m) => [`pdmode.${m.mode}`, {
      run: () => A.setPdmode?.(m.mode), on: (T?.pdmode ?? 34) === m.mode,
    } as AcadHandler])),
    "draw.pointstyle": { note: "pick the marker on the Point button's flyout — DDPTYPE's dialog "
                               + "is that list and nothing else" },
    // ⚠⚠ **BOTH BUTTONS MAKE A REAL `SPLINE`, AND THE DIFFERENCE IS WHAT THE CLICKS MEAN.**
    // The note here used to describe the design app, where Spline Fit made a polyline carrying
    // bulges — a biarc approximation with no control vertices to edit. In pdcad both author the
    // DXF entity: Fit stores the clicks AND the control frame computed through them, CV stores
    // the frame directly. Which one was used decides what the grips do afterwards, for good.
    "draw.spline.fit": {
      ...tool("draw.spline.fit"),
      note: "the curve passes exactly THROUGH the points you click, and the grips stay on them. "
          + "⚠ Spline CV beside it does the opposite",
    },
    "draw.spline.cv": {
      ...tool("draw.spline.cv"),
      note: "the clicks are CONTROL points — the curve is pulled toward them, not through them. "
          + "⚠ Spline Fit beside it does the opposite. Dragging a control vertex on a fit spline "
          + "discards its fit points, exactly as AutoCAD does",
    },
    // ⚠ Both flyout entries run the SAME command, because in AutoCAD they are the same
    // command with a different number of clicks. Rectangular is two corners; Polygonal is three
    // or more points and Enter. Freehand is the one that is genuinely not here — it needs a
    // dragged path, and a cloud traced from mouse samples is a different input, not a different
    // option.
    "draw.revcloud.rect": {
      ...tool("draw.revcloud"),
      note: "REVCLOUD — click two opposite corners for the rectangular cloud",
    },
    "draw.revcloud.free": {
      note: "Freehand tracing is not built. Click two corners for a rectangular cloud, or "
            + "three or more points and Enter for a polygonal one — both are REVCLOUD",
    },
    // ⚠⚠ MLINE is how AutoCAD drafters drew walls for twenty years, so this is the note that
    // actually gets read — and its old text pointed at a wall tool that is the DESIGN app's.
    // ⚠ Measured before deciding not to build it: MLINE is in 3.8% of 17,903 office drawings and
    // the MEDIAN of those is 0.02% of the drawable content — 4 drawings of 11,734 have it above
    // 5%. It is READ, carried and drawn (`recursive_decompose` explodes it into LINEs); what is
    // missing is only the tool that draws a new one.
    // ⚠ Built 2026-09-04. The note above it stays as the record of what was measured before
    // deciding — 3.8% of 17,903 drawings — because that measurement is why it was worth doing.
    "draw.mline": {
      ...tool("draw.mline"),
      note: "parallel lines in one object, on the Standard multiline style — the wall tool. "
          + "Its width is the whole thickness and the points you click are the TOP line, "
          + "which is AutoCAD's own default justification",
    },

    // ── Modify ──────────────────────────────────────────────────────────────────────────
    "mod.copy": { ...op("mod.copy"),
                  note: "duplicates at the offset on the command bar — not the clipboard" },

    // ⚠⚠ **THIS IS THE SAME COMMAND AS BREAK HERE, AND THE NOTE HAD IT BACKWARDS.** AutoCAD
    // has two: BREAK removes the segment between two picks, BREAKATPOINT splits at one and
    // leaves no gap. `edit._op_break` takes ONE point and splits into two pieces — so what is
    // built is BREAKATPOINT, and the button that said it was not is the one that does it.
    // ⚠⚠ **THE NOTE HERE WAS THE DESIGN APP'S AND IT IS NO LONGER TRUE.** It said "Break beside
    // it does the same thing here — this drawing's break has never removed a segment". pdcad's
    // BREAK now removes the span between two picks, in `trim_poly`'s own parameter space, so
    // the two commands are the different things AutoCAD has always had them be.
    // ⚠⚠ **BREAK TAKES TWO POINTS AND `PICK_OF` CAN ONLY OFFER ONE.** It routed through
    // `A.startPick("break", "point")` — a canvas mode this app does not have — so the command
    // reported "BREAK is not built yet" about an op whose server half was written and tested.
    "mod.break": {
      run: () => A.breakObject?.(false), arms: true,
      note: "cuts the span between two picks out of the selected object. ⚠ Select ONE object "
          + "first — the two points can only be on one of them. Break at Point beside it "
          + "splits without removing anything",
    },
    "mod.breakat": {
      run: () => A.breakObject?.(true), arms: true,
      note: "splits the selected object where you click and removes nothing — BREAK beside it "
          + "cuts the span between TWO picks. ⚠ Select one object first: the point can only be "
          + "on one of them",
    },
    // ⚠⚠ **"a one-line command line cannot" WAS TRUE OF THE OTHER APP AND IS NOT TRUE HERE.**
    // The note said to use the flyout because the design app had no way to ask for an option;
    // pdcad's command line does — `PEDIT ⏎` now prints AutoCAD's own
    // `[Close/Open/Width/Join/Reverse/…]` line and takes a letter. Leaving the note would have
    // left the one command in AutoCAD that IS a sub-menu as the one command here you could not
    // type, with an explanation for why that was inevitable.
    "mod.pedit": { run: () => A.runCmd?.("pedit"), arms: true,
                   note: "Close, Open, Width, Join, Reverse, or click a segment to bend, "
                         + "straighten, add a vertex to or delete one from" },
    // ⚠⚠ **A `window.prompt` FOR THE ORDER WAS THE DESIGN APP'S STAND-IN, AND SPLINEDIT IS NOT
    // A NUMBER — IT IS A SUB-MENU.** Like PEDIT, it takes a letter and then sometimes a point
    // and sometimes a number, and pdcad's command line can ask for all three. A modal box that
    // can only collect the degree made the other six options unreachable.
    //
    // ⚠ The old note also promised STRETCH would drag a control vertex. That was true of
    // `DesignShape`, whose `points` WERE its CVs. Here a control vertex is a GRIP.
    "mod.splinedit": { run: () => A.runCmd?.("splinedit"), arms: true,
                       note: "Close, Open, Purge the fit data, change the dEgree, or click to "
                             + "add or delete a control vertex. ⚠ Drag a vertex with its GRIP" },
    // ⚠⚠ **"DELETE THE HATCH AND RUN IT AGAIN" WORKS AND THROWS THE REGION AWAY.** For a floor
    // finish poured round three columns that is several minutes of re-tracing to change tile to
    // timber. A hatch here is `props.hatch.pattern` on the shape, so re-pointing it IS the
    // command — nothing needs deleting.
    "mod.hatchedit": {
      run: () => { toPlan(); A.hatchEdit?.(); },
      note: "changes the material of an already-hatched region to the one on the bucket, "
          + "keeping the region. ⚠ Select a hatched shape first — it will not pour a new one",
    },
    // ⚠ Twelve of these are submenu PARENTS whose children are all grey. A parent is drawn
    // live whenever any child is, so most parents never need a note — but one whose whole
    // submenu is unbuilt is itself a grey the user can hover, and "not built yet" on
    // Draw ▸ Arc would be a lie: the plain ARC command is built and on the ribbon.
    "draw.arcgroup": { on: true, note: "all eleven of AutoCAD's arc pick orders" },
    // ⚠ `A.makeBlock`, not `c.makeBlock` — the SAME repointing WBLOCK needed. The chrome's
    // version posts to `designMakeBlock`, which rejects; the studio's runs the real `block` edit
    // op and knows how to ask for a base point. Three entry points for one command and two of
    // them were pointed at the dead one.
    "draw.block": { run: () => A.makeBlock?.(), arms: false,
                    note: "same command as Block ▸ Create — the selection becomes a symbol and "
                        + "one instance is left standing in its place" },
    "file.import": {
      run: () => c.openImport(),
      note: "reads DWG and DXF. DGN, 3DS and the rest are not read",
    },
    "file.drawingutils": { note: "the drawing is in millimetres; there is nothing to audit "
                              + "or recover — it lives in Postgres, not in a file" },

    "mod.clip": { note: WHY["mod.clip"] },
    "mod.object": { note: WHY["mod.object"] },
    "ui.lockui": { note: "nothing in this shell floats, so there is nothing to lock" },
    // ⚠⚠ **"THERE IS NO XREF MANAGER" WAS TRUE OF THE OTHER APP.** In the design app an import
    // becomes that drawing's own shapes, so nothing stays referenced and the note was right.
    // A DXF is different: an external reference is a BLOCK record carrying a flag and a path,
    // it survives a round trip, and about one drawing in twenty-five of this office's archive
    // has one. There is a manager now, and it says what it can and cannot do.
    "insert.xref": { run: () => A.xrefs?.(),
                     note: "lists this drawing's external references and whether each resolves" },
    "file.closeall": { note: "one drawing is open at a time here, so there is no set of them to close" },
    "dwg.current": { on: true, note: "the drawing you are in" },
    "mod.union": { note: WHY["mod.union"] },
    "mod.subtract": { note: WHY["mod.subtract"] },
    "mod.intersect": { note: WHY["mod.intersect"] },
    "mod.divide": { note: WHY["mod.divide"] },
    // ⚠⚠ **FOUR MORE THAT DISPATCHED OPS THE BACKEND DOES NOT HAVE.** See the paragraph in
    // `OP_OF`: they were live buttons whose only possible outcome was "not built yet", found by
    // `test/routing.test.ts` rather than by a drafter. A refusal with a reason is an answer;
    // a live button that denies the command exists is not.
    "mod.align": { note: WHY["mod.align"] },
    "mod.lengthen": { note: WHY["mod.lengthen"] },
    // ⚠⚠⚠ **"there is one space here — Model" WAS THE DESIGN APP'S TRUTH, WRITTEN INTO pdcad.**
    // `models.Space`'s own docstring says it in as many words: `/opt/dsn/app` has one space,
    // "the reason CHSPACE, PSPACE and the layout tabs are meaningless there. Having real
    // layouts is a cost paid so those commands mean something." pdcad paid it — measured, 971
    // paper-space layouts across 144 drawings. Third instance of one defect class, after the
    // parametric tab's migration 0048 and `kernel.faces`.
    ...Object.fromEntries(["mod.chspace", "mod.changetospace"].map((id) => [id, {
      run: () => {
        const r = A.chspace?.();
        if (!r) { c.warn("CHSPACE is not wired to this canvas."); return; }
        if (r.why) { c.warn(r.why); return; }
      },
      note: "CHSPACE — moves the selection between model space and a paper layout, SCALED by "
            + "the viewport so it stays the same size on the sheet",
    } as AcadHandler])),
    // ⚠⚠ **`edit._op_array_path` HAS BEEN IN THE BACKEND ALL ALONG** — spacing by ARC LENGTH,
    // with an `align` that turns each clone to the local tangent. The note said "the
    // rectangular and polar arrays are built", which reads as "and this one is not". It is.
    // ⚠ Two picks, in order: what to array, then the path. That is why it is a pick op and not
    // a selection op — the path is not part of the selection, it is the second argument.
    "mod.array.path": {
      run: () => { toPlan(); A.pathArray?.(); },
      note: "distributes the selection along a picked curve, spaced by ARC LENGTH so items sit "
          + "evenly on a bend rather than evenly in x. Select the objects, pick the path, then "
          + "type the count — three steps, in that order",
    },
    "mod.clip.viewport": { note: "there is one viewport here — Model" },

    // ── Annotation ──────────────────────────────────────────────────────────────────────
    // ⚠ A NOTE ON A LIVE COMMAND, because the gesture differs from AutoCAD's: AutoCAD's MTEXT
    // asks for a RECTANGLE and wraps inside it. Ours asks for one point and the lines you type
    // are the lines you get — no wrap boundary, because there is no column width in the model.
    // Live rather than grey: a paragraph is a paragraph, and the difference is in how it is
    // bounded, not in what it produces.
    "anno.text.multi": { ...tool("anno.text.multi"),
                         note: "click once and type; Enter makes a line, Ctrl+Enter places it. "
                               + "AutoCAD drags a column width — ours has none" },
    // TEXTEDIT / DDEDIT — or just double-click the note.
    "mod.textedit": { run: () => A.editNote?.(),
                      note: "retypes the selected note — double-clicking it does the same" },
    // ⚠ Its old note said a schedule is generated on the data sheet "not drawn as a table object
    // on the plan" — true, and it described the gap rather than closing it. A door schedule
    // belongs ON the drawing. What this places is linework, and the note says so: AutoCAD's
    // TABLE is an editable entity and this is not one.
    "anno.table": { note: WHY["anno.table"] },
    "anno.table.link": { note: WHY["anno.table.link"] },
    // ── the dimension family: five of them now ──────────────────────────────────────────
    "anno.dim.aligned": dim("anno.dim.aligned",
      "pick two points, then pull the line clear — measures the true distance"),
    "anno.dim.linear": dim("anno.dim.linear",
      "pick two points, then pull the line clear — pull it up or down for the horizontal "
      + "measurement, sideways for the vertical"),
    "anno.dim.angular": dim("anno.dim.angular", "pick the vertex, then both arms"),
    "anno.dim.radius": dim("anno.dim.radius", "pick the centre, then a point on the arc"),
    "anno.dim.diameter": dim("anno.dim.diameter", "pick the centre, then a point on the arc"),
    // ⚠ Each says what IT would take, not just what exists. "Aligned, Angular, Radius and
    // Diameter are built" was true of all eight and told a reader nothing about any of them —
    // which of these is a five-minute job and which needs a datum model was invisible.

    // ⚠⚠ **THE DATUM IS FROZEN INTO THE DIMENSION, NOT READ FROM THE CURRENT UCS.** AutoCAD
    // resolves the origin every time it draws. This canvas's movable axes live in the BROWSER,
    // so a dimension resolving its datum at read time would show a different number to everyone
    // who opened the drawing — and a setting-out dimension that two people disagree about is
    // worse than no dimension at all. `p2` is the origin as it stood when the ordinate was
    // placed. See `dimension.plan_dimensions`.
    "anno.dim.ordinate": dim("anno.dim.ordinate",
      "click the point, then pull the label clear. Pull VERTICALLY for the X ordinate and "
      + "HORIZONTALLY for the Y — the leader runs across the axis it reads, as AutoCAD's does. "
      + "The datum is the drawing axes where they stand now, and it is written into the "
      + "dimension so it cannot drift"),
    "anno.dim.arc": dim("anno.dim.arc",
      "click on the arc — measures the length ALONG it, which is not the distance between "
      + "its ends"),
    // ⚠⚠ **ITS OLD GREY SAID "needs a jogged leader" — WHICH WAS A SPECIFICATION, NOT AN
    // EXCUSE, and it was right.** The leader is the whole command: a 40 m setting-out radius
    // has its centre three sheets away, so a plain DIMRADIUS draws a line off the page while
    // the figure quietly stays correct. `dimension.jog_points` computes the broken leader ONCE
    // and the canvas, the plot and the DXF all draw those same six points.
    "anno.dim.jogged": dim("anno.dim.jogged",
      "for the arc whose centre is off the sheet. Click the TRUE centre, a point on the arc, "
      + "then where the shortened leader should start — the figure stays the real radius and "
      + "the zig-zag says the leader is short"),
    // ⚠⚠ **THE OLD NOTE SAID "nothing here draws out of scale", AND THE MODEL CONTRADICTED
    // IT.** `DesignDimension.text` is documented as an override "for the rare 'clear opening'
    // or 'verify on site' case" — which is precisely a dimension whose figure is not what it
    // measures. Unmarked, that is the drawing error the jog exists to prevent, so it is now set
    // AUTOMATICALLY wherever an override disagrees with the geometry by more than a millimetre,
    // and this command is for the rest.
    // ⚠⚠⚠ **THE NOTE HERE WAS THE DESIGN APP'S AND DESCRIBED A FEATURE pdcad DOES NOT HAVE** —
    // "one whose text override already disagrees with what it measures is jogged for you". That
    // is the other app's generated setting-out chains. Replaced with the real blocker, measured
    // in ezdxf 1.4.4: DXF has no group code for a jog on a LINEAR dimension. `jog_point`
    // (group 15) belongs to ARC_DIMENSION alone; AutoCAD carries a linear jog as XDATA only
    // AutoCAD interprets. A zig-zag drawn as plain geometry would export as lines a client's
    // AutoCAD cannot recognise as a jog or straighten — worse than the command not existing.
    // ⚠⚠ **THE REFUSAL WAS RIGHT AND ITS EVIDENCE NAMED THE WRONG CLASS.** It said `jog_point`
    // (group 15) "belongs to `ARC_DIMENSION` alone". Re-checked in ezdxf 1.4.4 by
    // introspection, not by reading: `ARC_DIMENSION` does NOT support it. Group 15 belongs to
    // `AcDbRadialDimensionLarge` — AutoCAD's DIMJOGGED, the jogged RADIUS dimension, which is
    // built here (`anno.dim.jogged` above). A citation that is wrong in a refusal that is right
    // is how the next person to check inherits a false fact with a true conclusion attached.
    // ⚠ The conclusion is unchanged and re-verified: a LINEAR `DIMENSION` supports no jog
    // attribute at any version, so there is nowhere in the file to put one.
    "anno.dim.jogline": {
      note: "not built, and the blocker is in the FILE FORMAT rather than in the drawing: a "
          + "linear DIMENSION has no jog attribute at any DXF version, and AutoCAD carries the "
          + "jog as XDATA only AutoCAD reads. A zig-zag drawn as plain lines would export as "
          + "geometry your AutoCAD cannot recognise as a jog or straighten. ⚠ The JOGGED RADIUS "
          + "dimension is a different command and it IS built — see Jogged on this flyout",
    },
    // ⚠⚠ **IT IS A DRAWING SETTING, AND THAT IS FORCED BY WHAT THESE DRAWINGS ARE.** AutoCAD
    // stores a break on the dimension it belongs to. Most of the dimensions here are GENERATED
    // setting-out chains, rebuilt from the walls on every read — so a stored break is
    // structurally unable to reach them, which is exactly what the first version of this
    // command could not do. `plan.dim_break` plus `dimension.add_breaks` breaks every row,
    // generated and hand-added alike. Measured on a 17 416-shape import: 143 of 234 dimensions
    // gapped at 577 crossings, 213 ms.
    "anno.dim.break": { run: () => c.dimBreak(!!c.plan?.dim_break), on: !!c.plan?.dim_break,
                        note: c.plan?.dim_break
                          ? `DIMBREAK ▸ Remove — ${T?.dimsBroken || 0} dimension`
                            + `${T?.dimsBroken === 1 ? "" : "s"} are gapped where something `
                            + "crosses them. This clears every gap"
                          : "DIMBREAK ▸ Auto — gap every dimension line on this drawing, "
                            + "generated chains included, wherever anything crosses it" },
    // ⚠⚠ GREY, AND IT WAS WIRED THE WRONG WAY ROUND UNTIL THE GEOMETRY WAS READ. AutoCAD's
    // DIMLINEAR measures the horizontal or vertical PROJECTION between two points; ours measures
    // the true distance and draws the line PARALLEL to them (`dimShape`: a = p1+off, b = p2+off,
    // text = dist(p1,p2)), which is DIMALIGNED. On a sloped wall the two give different numbers,
    // so the wrong one would have been found on site rather than on screen.
    // ⚠ QDIM dimensions a whole selection in one go and picks the datums itself. Ours takes two
    // points and a pull, which is DIMLINEAR — so QDIM points at it rather than pretending.
    "anno.leader.align": {
      run: () => { const n = A.alignLeaders?.();
                   if (!n) c.warn("Select two or more leaders to align."); },
      note: "lines the NOTES up in a column and leaves every arrow where it points",
    },
    // ⚠⚠ **THE REASON THIS WAS GREY HAD QUIETLY BECOME FALSE.** It said a tag could not be told
    // from a sentence because `props.frame` was "not a typed field" — and `frame` has carried
    // "box" / "circle" / "none" on every leader since the tag tool shipped. That IS the field.
    // A grey whose reason has expired is worse than one with no reason at all, because it reads
    // as considered and nobody goes back to re-check it.
    "anno.leader.collect": {
      run: () => {
        const r = A.collectLeaders?.();
        if (!r) return;
        if (r.why) { c.warn(r.why); return; }
        c.warn(`${r.n} tags collected into one note with ${r.n} arrows.`);
      },
      note: "merges the selected TAGS into one note reading \u201cSO-1, SO-2, SO-3\u201d and keeps "
            + "every arrow. Framed tags only — collecting sentences would make a paragraph",
    },

    "anno.style.table": { note: WHY["anno.style.table"] },
    // ⚠ Its old note said "worth building — nothing indexes note text yet". Nothing needed to:
    // the notes are in the model the canvas already holds, so the search is a filter, not an index.
    "anno.text.find": { run: () => c.openFind(),
                        note: "finds a note by its text and selects every match" },
    // ⚠ DIMSPACE evens out an existing stack; there is nothing to pick, so it acts on the
    // drawing's dimensions directly and says how many it moved.
    "anno.dim.space": { run: () => { const n = A.dimSpace?.(); 
                                     if (!n) c.warn("Place two or more linear dimensions first."); },
                        note: "evens out the gaps in a stack of dimensions" },
    // ⚠⚠ **THE STYLE MANAGERS OPEN THE PANEL THAT ACTUALLY HOLDS THE STYLES.** Same decision as
    // PROPERTIES being the studio's inspector and the Layer Properties Manager being its Layers
    // panel: a second styles dialog would drift from the real one within a week. Height, pen,
    // weight and dash all live on a PEN STYLE here, which the Pen Styles palette edits.
    // ⚠⚠⚠ **THIS PAIR WAS THE LIFTED-CHROME MISTAKE AGAIN.** Both opened the DESIGN app's Pen
    // Styles palette with a note — "height, pen, weight and dash live on a style here" — that
    // is true of the app the chrome came from and false of this one. In a DXF a STYLE is a
    // symbol-table row naming a font FILE, a fixed height, a width factor and an oblique, and
    // 972 of them are stored on this box across 57 fonts. A pen style cannot express any of it.
    // Same shape as greying GROUP because "a group here is a BLOCK".
    "anno.style.text": {
      run: () => A.textStyle?.(),
      note: "the drawing's TEXT styles — font file, fixed height, width factor and oblique — "
            + "with a live sample in the font the canvas will actually use",
    },
    // ⚠ MLEADERSTYLE is NOT the same table. It is a dictionary OBJECT, not a symbol-table row,
    // and 224 of them are carried here and round-trip untouched. Saying that is honest; sending
    // a drafter to a text-style dialog that cannot change an arrowhead is not.
    "anno.style.mleader": {
      note: "a multileader style is a dictionary object, not a symbol-table row — the 224 "
            + "carried in these drawings round-trip untouched and nothing here edits one. "
            + "Text Style… on this panel is the one that IS editable",
    },
    // ⚠⚠ **DIMSTYLE IS ITS OWN MANAGER HERE, AND THE PEN-STYLES NOTE ABOVE IS WRONG FOR IT.**
    // That note is true of the DESIGN app, where a pen style carries height and weight. In a
    // DXF a DIMSTYLE is a symbol-table row of ~80 variables that a client's house standard
    // lives in, and `dimscale` is the annotative mechanism every drawing this office receives
    // actually uses — none of which a pen style can express.
    "anno.style.dim": { run: () => A.dimStyle?.(),
                        note: "text height, arrow size, decimals and DIMSCALE — the overall "
                              + "scale that makes a dimension print at nominal size through a "
                              + "viewport" },
    "anno.text.spell": { note: WHY["anno.text.spell"] },
    // ⚠⚠ **THE ONE REAL GAP ON THE ANNOTATION SCALE PANEL, and the other six were greyed by a
    // note that was simply wrong.** They said "Nothing here is annotative"; a note's height has
    // always been in PAPER millimetres and the viewport's scale has never touched it. What was
    // missing is the OVERRIDE: the height came from the LAYER's pen, so one note could not be
    // made bigger without changing every note on that layer.
    // ⚠ 0 puts it back on the pen, which is AutoCAD's own "match the style" and the only way
    // out of an override that would otherwise be permanent.
    "anno.text.scale": {
      run: () => {
        const n = (c.st?.selShapes || []).length;
        if (!n) { c.warn("Select a note or a leader first."); return; }
        // eslint-disable-next-line no-alert -- one number, and the same honest stand-in the
        // block name uses. The command bar's Distance box belongs to a running TOOL.
        const raw = window.prompt("Text height in PAPER millimetres (0 = use the layer's pen)",
                                  "3.5");
        if (raw === null) return;
        const mm = parseFloat(raw);
        if (!Number.isFinite(mm) || mm < 0) { c.warn("Height must be a number, or 0."); return; }
        const done = A.setTextHeight?.(mm) || 0;
        c.warn(done
          ? `${done} note(s) set to ${mm > 0 ? `${mm} mm on paper` : "the layer's pen height"}`
          : "Nothing selected was a note or a leader.");
      },
      note: "sets the selected notes' height in PAPER millimetres, overriding the layer's pen. "
          + "0 puts them back on it",
    },
    "anno.addscale": { note: WHY["anno.addscale"] },
    "anno.syncscale": { note: WHY["anno.syncscale"] },
    "anno.visibility": { note: WHY["anno.visibility"] },

    // ── Layers ──────────────────────────────────────────────────────────────────────────
    "layer.props": pal("layer.props"),
    // ⚠ A LIVE DROPDOWN, and the one control on the Layers panel that a drafter uses every
    // minute: which layer the next line lands on. It is the studio's own `drawLayer`, so the
    // ribbon and the tool-options strip cannot disagree about it.
    "layer.control": {
      select: {
        value: T?.drawLayer ?? "",
        options: (c.plan?.layers?.length ? c.plan.layers : [{ name: "A-DETL" }])
          .map((l: any) => ({ value: l.name, label: l.name })),
        onChange: (v: any) => A.setDrawLayer?.(v),
      },
      note: "the layer the next line, polyline, rectangle or circle is drawn on",
    },
    // ⚠⚠ **THE SIXTEEN LAYER TOOLS, AND WHY THEY ARE NOT THE LAYER PALETTE AGAIN.** They were
    // all greyed `IN_LAYER_PAL` — "layers are managed in the Layer Properties Manager" — which
    // is TRUE and answers a question nobody asked. The palette starts from a NAME: it lists what
    // exists and lets you set properties on a row. Every command here starts from a THING you
    // clicked and never makes you say the name at all. That is not a shortcut to the palette, it
    // is the opposite direction, and it is how a drafter actually works: point at the wall that
    // is in the way, do not go and find "A-WALL" in a list of forty.
    //
    // ⚠ Each one writes through `/design/layers/<id>/` and then re-reads the drawing — see
    // `patchLayers`. The canvas draws the layers the PAGE fetched, so a local edit would look
    // right until the next reload and then revert.
    ...(() => {
      const selLayers = (): string[] => A.selectionLayers?.() || [];
      const names = (): string[] => (c.layers || []).map((l: any) => l.name);
      /** A layer command that acts on the SELECTION's layers. */
      const onSel = (patch: any, verb: string, note: string): AcadHandler => ({
        run: () => {
          const ls = selLayers();
          if (!ls.length) { c.warn("Select an object on the layer first."); return; }
          const n = c.patchLayers(ls, patch);
          c.warn(`${verb} ${n} layer${n === 1 ? "" : "s"}: ${ls.join(", ")}`);
        },
        note,
      });
      return {
        // LAYMCUR — the layer picker in the ribbon, driven from the drawing instead.
        "layer.makecurrent": {
          run: () => {
            const ls = selLayers();
            if (!ls.length) { c.warn("Select an object first."); return; }
            if (ls.length > 1) {
              c.warn(`The selection spans ${ls.length} layers — pick one object.`); return;
            }
            A.setDrawLayer?.(ls[0]);
            c.warn(`${ls[0]} is now the current layer.`);
          },
          note: "makes the selected object's layer the one the next line is drawn on",
        },
        // LAYERP. ⚠ Greys when there is nothing to put back, which is the one honest use of
        // grey-for-state in this shell: "no previous layer state" is not a missing feature.
        "layer.previous": {
          ...(c.canLayerPrev ? { run: () => { c.layerPrev(); } } : {}),
          note: c.canLayerPrev ? "undoes the last layer on/off/lock change"
                               : "nothing to put back — no layer has been switched this session",
        },
        // LAYISO — everything except the selection's layers goes off.
        "layer.iso": {
          run: () => {
            const keep = selLayers();
            if (!keep.length) { c.warn("Select an object on the layer to isolate."); return; }
            const off = names().filter((n) => !keep.includes(n));
            if (!off.length) { c.warn("That is already the only layer."); return; }
            c.patchLayers(off, { visible: false });
            c.warn(`Isolated ${keep.join(", ")} — ${off.length} layers off. `
                   + "Layer ▸ Unisolate puts them back.");
          },
          note: "turns every OTHER layer off. Unisolate — or Previous — puts them back",
        },
        "layer.unisolate": {
          ...(c.canLayerPrev ? { run: () => { c.layerPrev(); } } : {}),
          note: c.canLayerPrev ? "puts back the layers Isolate turned off"
                               : "nothing is isolated",
        },
        "layer.off": onSel({ visible: false }, "Turned off",
                           "turns off the layer of whatever is selected"),
        // ⚠⚠ **FREEZE AND OFF ARE THE SAME THING HERE, AND THE TOOLTIP SAYS SO RATHER THAN THE
        // BUTTON BEING GREY.** In AutoCAD they differ in REGENERATION and in how an xref's
        // layers behave — a frozen layer is skipped by REGEN and is not loaded at all. This
        // canvas has no regeneration pass to skip and resolves xrefs at import time, so the two
        // produce an identical drawing. Greying FREEZE would deny a drafter the command they
        // reach for; wiring it silently would be the "quietly different" failure this table's
        // header warns about. Wiring it and SAYING it is the third option.
        "layer.freeze": onSel({ visible: false }, "Froze",
                              "identical to Off here — there is no regeneration pass to skip, "
                              + "so a frozen and an off layer draw the same drawing"),
        "layer.lock": onSel({ locked: true }, "Locked",
                            "visible but not selectable — draw over it without grabbing it"),
        "layer.unlock": onSel({ locked: false }, "Unlocked", "makes the layer selectable again"),
        "layer.on": {
          run: () => {
            const off = (c.layers || []).filter((l: any) => !l.visible).map((l: any) => l.name);
            if (!off.length) { c.warn("Every layer is already on."); return; }
            c.patchLayers(off, { visible: true });
            c.warn(`${off.length} layer${off.length === 1 ? "" : "s"} back on.`);
          },
          note: "turns every layer in the drawing back on",
        },
        // LAYCUR — move the selection onto the CURRENT layer.
        "layer.tocurrent": {
          run: () => {
            const to = T?.drawLayer;
            if (!to) { c.warn("No current layer."); return; }
            const n = A.moveToLayer?.(to) || 0;
            c.warn(n ? `${n} object${n === 1 ? "" : "s"} moved to ${to}.`
                     : "Select the objects to move first.");
          },
          // ⚠ The old note described the DESIGN app, where a wall takes its layer from its kind.
          // A DXF entity carries its own layer and every one of them moves.
          note: "moves the selection onto the layer in the picker",
        },
        // LAYMCH — move the selection onto a layer you choose.
        "layer.match": {
          run: () => {
            if (!st?.selected) { c.warn("Select the objects to move first."); return; }
            c.openLayerDlg({
              mode: "pick", title: "Change to Layer", verb: "Move",
              hint: "the selected objects move to this layer",
              onPick: (to: string) => {
                const n = A.moveToLayer?.(to) || 0;
                c.warn(n ? `${n} object${n === 1 ? "" : "s"} moved to ${to}.`
                         : "Nothing moved.");
              },
            });
          },
          note: "moves the selection to a layer you pick",
        },
        // COPYTOLAYER. ⚠ The originals STAY — that is the whole command: keep the setting-out
        // and get a copy on the plotted layer.
        "layer.copyto": {
          run: () => {
            if (!st?.selected) { c.warn("Select the objects to copy first."); return; }
            c.openLayerDlg({
              mode: "pick", title: "Copy Objects to New Layer", verb: "Copy",
              hint: "a copy lands on this layer; the originals stay where they are",
              onPick: (to: string) => {
                const n = A.copyToLayer?.(to) || 0;
                c.warn(n ? `${n} object${n === 1 ? "" : "s"} copied to ${to}.` : "Nothing copied.");
              },
            });
          },
          note: "leaves the originals and puts a copy on another layer",
        },
        "layer.walk": {
          run: () => c.openLayerDlg({
            mode: "walk", title: "Layer Walk",
            // ⚠⚠ **THE ESCAPE HATCH IS NAMED BECAUSE THE RESTORE HAS ONE HOLE.** Closing the
            // dialog, pressing Escape and clicking the backdrop all unmount the component and
            // put the layers back. CLOSING THE BROWSER does not — React never unmounts, the
            // cleanup never runs, and the drawing is left in the database with one layer
            // showing. Verified: 0 layers off → 11 off mid-walk → 0 off after Close; and a
            // probe that killed the browser mid-walk left all 11 off. So the way out is on
            // the dialog rather than in a release note.
            hint: "one layer at a time. Close puts every layer back as it was — if the browser "
                  + "is closed mid-walk instead, Layers ▸ Turn All Layers On.",
          }),
          note: "shows one layer at a time — the fastest way to find what is on what",
        },
        "layer.merge": {
          run: () => c.openLayerDlg({
            mode: "merge", title: "Merge Layers", verb: "Merge",
            hint: "everything on the first layer moves to the second, and the first is deleted",
          }),
          note: "moves everything off one layer onto another and deletes the empty one",
        },
        "layer.delete": {
          run: () => c.openLayerDlg({
            mode: "delete", title: "Delete Layer", verb: "Delete",
            hint: "the layer AND everything drawn on it",
          }),
          note: "⚠ erases the layer and every object on it",
        },
        "layer.states": {
          run: () => c.openLayerDlg({
            mode: "states", title: "Layer States Manager",
            hint: "a named snapshot of which layers are on and which are locked",
          }),
          note: "saves and restores which layers are on. ⚠ Kept in THIS BROWSER, not in the "
                + "drawing — a colleague opening the same plan does not see your states",
        },
      } as AcadHandlers;
    })(),

    // ── Blocks ──────────────────────────────────────────────────────────────────────────
    "block.insert": {
      // ⚠ INSERT is genuinely block insertion: pick a block, click where it goes. Ours takes
      // the block from the Blocks palette, so pressing INSERT opens it — the command would
      // otherwise arm a tool with nothing loaded and look broken exactly once.
      run: () => { c.showPalette("pal.blocks", true); c.setMode("furniture"); },
      arms: true,
      note: "pick the block on the Blocks palette, then click the drawing",
    },
    // ⚠⚠ **THE LIBRARY STOPPED BEING READ-ONLY AND THE NOTE DID NOT NOTICE.** These said the
    // symbol library is curated and there is nowhere for a user-made block to go —
    // `blocks.create_from_selection` has put one there since the Rayon lane got BLOCK, and it
    // was simply never reachable from this shell. Same shape as the black model space: built,
    // and greyed by a paragraph that had aged out.
    // ⚠⚠ **THROUGH `A`, NOT `c.makeBlock`.** The chrome's own `makeBlock` maps `selShapes`
    // through `Number()` — the other app's ids were integers and pdcad's handles are hex
    // strings like `4A1`, so `Number("4A1")` is NaN, the id list came out empty and it warned
    // "select geometry first" with a full selection on screen. It also has nowhere to ask for a
    // BASE POINT, which a block cannot be made without.
    "block.create": {
      run: () => A.makeBlock?.(),
      note: (c.st?.selShapes || []).length
        ? `makes a symbol from the ${(c.st?.selShapes || []).length} selected object(s) and `
          + "leaves ONE instance standing where they were"
        : "makes a symbol from the selected geometry — select something first",
    },
    // ⚠⚠⚠ **THIS CALLED THE CHROME'S `makeBlock`, WHICH IS BACKED BY A REJECTING STUB.**
    // `block.create` two entries up already routes to `A.makeBlock` — the STUDIO action, on the
    // real `block` edit op — and says in its own comment why. WBLOCK was left pointing at
    // `c.makeBlock`, which calls `designMakeBlock`, which is `soon("BLOCK")` and rejects. So two
    // buttons for the same act sat side by side, one working and one throwing an endpoint error,
    // and BOTH notes described working behaviour.
    // ⚠ Found by auditing every command whose shell action is stub-backed, after the Parametric
    // tab turned out to claim — with a migration number — that it worked. Same shape of fault:
    // the live path was built and the second entry point was never repointed at it.
    // ⚠ WBLOCK's only honest meaning without files: it writes to the SHARED library and KEEPS
    // the geometry on this drawing, which is what writing a copy out to a .dwg does.
    "block.wblock": {
      run: () => A.makeBlock?.(),
      note: "the same command as Create Block. AutoCAD's WBLOCK writes a .dwg; there are no "
          + "files here, so it makes the symbol and leaves one instance standing — and the "
          + "geometry it was made from is what that instance draws",
    },
    // ⚠⚠ **ATTRIBUTES ARE WHAT MAKE A BLOCK COUNTABLE RATHER THAN JUST REPEATABLE.** The old
    // note said a block's fields ARE its catalogue parameters — true of a catalogue block, and
    // no help at all for a handle somebody drew that has to carry a supplier code. Tags now live
    // on the definition and values on each instance (`blockattr`), and the schedule groups by
    // the VALUES, so forty handles under two codes are two purchase lines.
    // ⚠⚠ **ALL FOUR POINTED AT `c.openAttrs`, WHICH IS THE DESIGN APP'S PANEL AND DOES NOT
    // EXIST HERE.** In pdcad an attribute VALUE is a row in the Properties palette — that is
    // where a drafter changes forty room tags at once — and a DEFINITION is a list to read.
    // Measured: ATTDEF is in 43.5% of this office's drawings and ATTRIB in 47.6%, so these
    // four buttons are not a long-tail nicety.
    "block.attmanage": {
      run: () => A.blockAtts?.(),
      note: "lists every block's attribute tags, prompts and which are constant — including "
          + "blocks nothing has placed yet. ⚠ To CHANGE a value, select the block: its "
          + "attributes are rows in Properties",
    },
    "block.edit": {
      run: () => A.blockAtts?.(),
      note: "an attribute's value is a row in the Properties palette — select the block and "
          + "type over it. Select forty and they all change at once",
    },
    // ⚠⚠ **LIVE — and the note above it was falsified by the commit two panels up.** It used to
    // say a block's body is "curated in the library rather than edited in a drawing", which is
    // true of the 630 catalogue symbols and false of every block BLOCK/WBLOCK now lets a
    // designer make. The excuse outlived the condition it described by four commits.
    //
    // ⚠ There is no second canvas. BEDIT explodes the definition where its instance stood, you
    // edit it with the drawing's own tools, and Save Block re-authors it — so undo, snaps,
    // layers and the command line all work inside a block edit because they are the same ones.
    // ⚠⚠ **`A.bedit*`, NOT `c.bedit*` — the chrome's pair posts to `designBlockEditOp`, which
    // is a REJECTING STUB, and expects the design app's answer (`w_mm`, `d_mm`, a catalogue
    // `code`) that pdcad does not have. Third command found pointed at a dead path after BLOCK
    // and WBLOCK; here the live one is an edit op, so BEDIT gets undo and the command line free.
    "block.editor": {
      // ⚠⚠ **THE REFUSAL HAS TO BE SAID, AND THE FIRST VERSION DROPPED IT ON THE FLOOR.**
      // `A.beditOpen` returns `{why}` when the selection is not exactly one block — and this
      // ran it for its side effect and threw the answer away, so BEDIT with nothing selected
      // printed its own echo and NOTHING ELSE. That is the silent-button fault this app has
      // now found four times; a browser scenario caught it within a minute of being written.
      run: () => {
        const r = c.st?.bedit ? A.beditClose?.(true) : A.beditOpen?.();
        if (r?.why) c.warn(r.why);
      },
      on: !!c.st?.bedit,
      note: c.st?.bedit
        ? `${c.st?.bedit} is open — press again to save it, or Discard to throw the edit away`
        : "opens a block's definition ON THE DRAWING, where its instance stood — there is no "
          + "second canvas, so undo, snaps and the command line all work inside the edit. "
          + "⚠ Select a placed block first, and saving changes EVERY placed copy",
    },
    "block.savedef": {
      run: c.st?.bedit ? () => { const r = A.beditClose?.(true); if (r?.why) c.warn(r.why); }
                       : undefined,
      note: c.st?.bedit ? `saves ${c.st?.bedit} and puts its instance back`
                    : "saves the block being edited — nothing is open for editing",
    },
    "block.discard": {
      run: c.st?.bedit ? () => A.beditClose?.(false) : undefined,
      note: c.st?.bedit ? `throws away the edit to ${c.st?.bedit} and restores it as it was`
                    : "discards a block edit — nothing is open for editing",
    },
    "block.setbase": { note: WHY["block.setbase"] },
    "block.recent": { note: WHY["block.recent"] },
    "block.favourites": { note: WHY["block.favourites"] },

    // ── Parametric ──────────────────────────────────────────────────────────────────────
    //
    // ⚠⚠ **THE WHOLE TAB WAS GREY, AND HALF OF IT DID NOT NEED TO BE.** AutoCAD's constraint is
    // a LIVING relationship: constrain two lines parallel and moving one turns the other, for
    // ever, with a padlock glyph on the drawing to prove the relationship is still there. That
    // needs a constraint table, a solver and a rule for what happens when two constraints fight
    // — a product, not an afternoon, and it is why the tab was greyed wholesale.
    //
    // But a drafter pressing PARALLEL almost always wants these two lines parallel NOW. That
    // half is a geometric correction and it is built. The ten below apply once and stop; the
    // eight that are inseparable from persistence stay grey and say which half is missing.
    //
    // ⚠ The distinction is IN EVERY TOOLTIP, not in a release note. "Applied once" is the first
    // thing each one says, because a drafter who assumes the AutoCAD meaning will edit one line
    // an hour later and find the other did not follow — and will read that as a bug in the
    // constraint rather than as the documented limit of it.
    ...Object.fromEntries(([
      ["coincident", "coincident", "moves each object's nearest end onto an end of the first"],
      ["collinear", "collinear", "puts every other selected line on the first one's line"],
      ["concentric", "concentric", "moves the other circles onto the first one's centre"],
      ["parallel", "parallel", "turns every other selected line parallel to the first"],
      ["perpendicular", "perpendicular", "turns the other lines square to the first"],
      ["tangent", "tangent", "slides the circle until its edge just touches the line"],
      ["horizontal", "horizontal", "lays the selected lines flat"],
      ["vertical", "vertical", "stands the selected lines up"],
      ["equal", "equal", "makes the other lines the same length as the first"],
      ["autoconstrain", "auto", "lays down any line already within 1° of an axis"],
    ] as [string, string, string][]).map(([id, kind, what]) => [`par.${id}`, {
      // ⚠⚠ **THE COUNT COMES FROM THE SERVER, NOT FROM THE SELECTION SIZE.** This used to
      // print `r.n` — the number of objects SENT — the instant the action returned, before the
      // edit had been applied. A constraint refuses what it cannot act on (a hatch cannot be
      // made parallel) and skips what already satisfies it, so "4 objects constrained." was
      // routinely wrong about a selection of four. `A.constrain` reports the real number when
      // the edit comes back; the only thing left to say here is a refusal.
      run: () => {
        const r = A.constrain?.(kind);
        if (!r) { c.warn("constrain is not wired to this canvas."); return; }
        if (r.why) { c.warn(r.why); return; }
      },
      // ⚠ Pivots about each shape's own middle, and the tooltip says so: AutoCAD anchors on the
      // point you picked first, a selection has no pick order, and a line that swung across the
      // drawing to satisfy a constraint would read as a bug rather than as a constraint.
      note: `applied ONCE, not held — ${what}. It pivots about each line's own midpoint, and `
            + "a later edit will NOT drag the others after it the way AutoCAD's would",
    } as AcadHandler])),
    // ⚠⚠⚠ **THIS PARAGRAPH DESCRIBED ANOTHER APPLICATION, AND IT WAS BELIEVED FOR THREE DAYS.**
    // It said the dimensional half "exists now", that `DesignConstraint` + `DesignParam`
    // (migration 0048) were it, and that changing a value in the Parameters Manager moves the
    // geometry. All of that is true — of the DESIGN app at /opt/dsn, whose migrations reach
    // 0048. pdcad's stop at 0008, it has no constraint model of any kind, and `api.ts` says so
    // in its own header: "pdcad genuinely has no sheet sets, no lights, no surfaces and no
    // parametric constraints." `designParametricPost` is a stub that REJECTS.
    // ⇒ So DIMCONSTRAINT, the Parameters Manager, the constraint bars and DELCONSTRAINT are
    // NOT built here. The ten GEOMETRIC constraints above now are — `edits.do_constrain`, one
    // shot, no solver — and that is a different and much smaller thing than this claimed.
    // ⚠ A comment copied between two sibling apps is worse than no comment: it reads as a
    // measured fact and it is a lie with a migration number attached.
    // ⚠ These three drove `designParametricPost` / `designParametricDel`, which are REJECTING
    // stubs — so each one threw into `ctx.run`'s catch and reported a raw endpoint error. A
    // refusal that names the reason is the honest form of the same answer.
    "par.showall": { run: () => c.constraintBar(true), note: CONSTRAINT_BAR },
    "par.hideall": { run: () => c.constraintBar(false), note: CONSTRAINT_BAR },
    "par.delcon": {
      run: () => c.delConstraints(),
      note: "DELCONSTRAINT — removes the dimensional constraints on the selected objects, or "
            + "all of them when nothing is selected. Named parameters are left alone",
    },
    // ⚠⚠ **FOUR BUTTONS, FOUR KINDS, ONE COMMAND — and the ribbon's own labels decide which.**
    // AutoCAD's Linear, Aligned, Angular and Radial are one DIMCONSTRAINT with an option, and
    // splitting them into four handlers that each build a different body is how the two halves
    // of one command drift apart.
    ...Object.fromEntries(Object.entries({
      "par.dimconstraint": "linear", "par.dim.aligned": "aligned",
      "par.dim.angular": "angular", "par.dim.radial": "radial",
    }).map(([id, kind]) => [id, {
      run: () => c.addConstraint(kind),
      note: `${kind.toUpperCase()} — takes the size the geometry ALREADY has and holds it. `
            + "Change the value in the Parameters Manager and the geometry moves. "
            + NOT_A_SOLVER,
    } as AcadHandler])),
    "par.geomgroup": {
      note: "the ten geometric constraints — each applies once and leaves nothing behind. "
            + NOT_A_SOLVER,
    },
    "par.paramgr": {
      run: () => c.openParams(),
      on: c.paramsOpen,
      note: "PARAMETERS — the constraints and the named numbers they can be written in terms "
            + "of, in one grid. The Measured column is what the geometry reads right now; when "
            + "it disagrees with Value the constraint has stopped driving",
    },

    // ── Properties ──────────────────────────────────────────────────────────────────────
    "prop.palette": pal("prop.palette"),
    // ⚠⚠ **THE OLD GREY POINTED AT THE TOOL-OPTIONS STRIP, AND THAT IS A DIFFERENT CONTROL.**
    // The strip sets what the NEXT thing you draw will look like; these set what you have
    // ALREADY SELECTED. Every CAD has both because they answer different questions, and
    // sending a drafter from one to the other was sending them somewhere that could not do
    // what they had asked for. Stored as `props.colour` / `lw` / `ltype`, resolved by
    // `draw._pen` and `penOf` in the same order AutoCAD resolves them: per-object, then the
    // style, then ByLayer.
    // ⚠ ByLayer DELETES the key. `_merge_props` treats a falsey value as a deletion, so a
    // stored "" would be a second way of saying ByLayer that looks different in the JSON.
    "prop.colour": {
      select: {
        value: T?.pen?.colour || "",
        options: [{ value: "", label: "ByLayer" },
                  ...PEN_COLOURS.map(([v, l]) => ({ value: v, label: l }))],
        onChange: (v: any) => A.setPen?.("colour", v),
      },
      note: "the colour of the SELECTED objects. ⚠ Not the strip above the drawing — that one "
          + "sets what you are about to draw. ByLayer puts them back on the layer's pen",
    },
    "prop.lineweight": {
      select: {
        value: T?.pen?.lw ?? "",
        options: [{ value: "", label: "ByLayer" },
                  ...LINEWEIGHTS.map((n) => ({ value: n, label: `${n.toFixed(2)} mm` }))],
        onChange: (v: any) => A.setPen?.("lw", v === "" ? "" : Number(v)),
      },
      note: "the pen weight of the SELECTED objects, in PAPER millimetres — so it plots the "
          + "same at 1:20 and 1:100. ⚠ AutoCAD's ladder, not a free number: a drawing whose "
          + "pens are 0.24 and 0.26 has no pen weights at all",
    },
    "prop.linetype": {
      select: {
        value: T?.pen?.ltype || "",
        options: [{ value: "", label: "ByLayer" },
                  ...Object.keys(LTYPE_DASH).map((n) => ({ value: n, label: n }))],
        onChange: (v: any) => A.setPen?.("ltype", v),
      },
      note: "the linetype of the SELECTED objects. ⚠ Stored by NAME, so the DXF carries HIDDEN "
          + "as HIDDEN rather than as a dash pattern nobody can name back",
    },
    // ⚠⚠ **ITS OLD GREY SAID "per-object transparency is not stored" AND THAT WAS THE WHOLE
    // TASK, WRITTEN OUT.** It is stored now — `props["alpha"]`, on AutoCAD's own scale of
    // percent TRANSPARENT, 0..90 — and all three writers read it: the canvas as SVG opacity,
    // the plot as a reportlab alpha on BOTH fill and stroke, and the DXF as real group-code-440
    // transparency, which is the one that survives being opened in AutoCAD and edited back.
    // ⚠ 90 is AutoCAD's ceiling, not an arbitrary one: at 100 the object is invisible, and an
    // invisible object you cannot select is indistinguishable from one somebody deleted.
    "prop.transparency": {
      select: {
        value: T?.alpha ?? 0,
        options: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90].map((n) => ({
          value: n, label: n ? `${n}%` : "Opaque" })),
        onChange: (v: any) => A.setAlpha?.(Number(v)),
      },
      note: "how transparent the SELECTED objects are — 0 is solid, 90 is AutoCAD's limit. "
          + "⚠ Per object, not per layer, and it goes out in the DXF as real transparency",
    },
    // ⚠ MATCHPROP arms a MODE rather than acting on the selection: it needs a source object and
    // then a run of targets, which is two gestures, not a command with an argument.
    "prop.match": { run: () => { c.setMode("select"); A.armMatchProp?.(); }, arms: true,
                    note: "click the object to copy FROM, then every object to paint" },
    "prop.list": { note: "the Properties palette shows what is selected" },

    // ── Groups, Utilities ───────────────────────────────────────────────────────────────
    // ⚠ AutoCAD's MEASUREGEOM opens on DISTANCE and offers the rest as options; the big button
    // does the same, and its flyout is where the other three live. A keyword prompt for four
    // modes that already have their own buttons would be a second way to reach the same thing.
    "anno.leader.add": {
      // ⚠⚠⚠ **MEASURED, AND THE ANSWER IS WHY IT IS NOT BUILT.** A MULTILEADER this app authors
      // draws from its own data — verified: 3 polys and 1 text, owned by the entity. A CLIENT's
      // draws from its PROXY GRAPHICS blob, which is what `display.py` renders and what this app
      // cannot regenerate. So editing the leader list would change the file and NOT the picture,
      // on exactly the drawings that matter. A command that appears to work and does not is
      // worse than one that says it does not.
      note: "not built — adding a leader line would change the file and NOT the drawing. A "
          + "client's MULTILEADER is drawn from its proxy-graphics blob, which this app can "
          + "read and cannot regenerate, so the new leader would be invisible until AutoCAD "
          + "opened it",
    },
    "util.measure": {
      run: () => { toPlan(); A.measureDist?.(); }, arms: true,
      note: "measures point to point. ⚠ Radius, Angle and Area are on this button's flyout, "
          + "and AREA and DIST are commands of their own",
    },
    "util.measure.radius": {
      run: () => A.measureRadius?.(),
      note: "select a circle or an arc — the radius, the diameter and the circumference "
          + "together, which is what AutoCAD prints",
    },
    "util.measure.angle": {
      // ⚠ The note here used to say an angle readout "is not built — DIMANGULAR draws one as a
      // dimension instead". It is now, and the two are different acts: this MEASURES, DIMANGULAR
      // puts a permanent annotation on the drawing.
      run: () => { toPlan(); A.measureAngle?.(); }, arms: true,
      note: "three points, and the VERTEX is the second. ⚠ Three rather than two lines: two "
          + "lines make four angles and AutoCAD has to ask which one you meant",
    },
    "util.measure.volume": { note: WHY["util.measure.volume"] },
    "util.measure.quick": { note: "Distance (DIST) and Area (AREA) are built" },
    "util.quickselect": { run: () => c.openQSelect(),
                          note: "select every object of a kind, or everything on a layer" },
    "util.calc": { note: WHY["util.calc"] },
    "util.id": { note: "the cursor's coordinates read out bottom-left, in millimetres" },
    "util.purge": { note: WHY["util.purge"] },
    "util.selectsimilar": { run: () => A.selectSimilar?.(),
                            note: "selects everything of the same kind on the same layer" },
    // ⚠⚠ **LOCAL — take the drawing into this tab.** Its own handler rather than an `OP_OF`
    // entry, because `op()` asks for a SELECTION first: this is a document command, like SETVAR,
    // and there is nothing to pick. The studio says what it did, both ways.
    "util.local": { run: () => A.runCmd?.("local"),
                    note: "the drawing is held in the browser and edited there — no round trip" },
    // ⚠ RENDERER prints the canvas backend on the command line — see `Studio.tsx`. A document
    // command with nothing to pick, so its own handler rather than an `OP_OF` entry.
    "util.renderer": { run: () => A.runCmd?.("renderer"),
                       note: "which canvas backend is drawing, and what the driver calls itself" },
    // ⚠ SERVER hands it back — see `Studio.tsx`. Its own handler for the same reason LOCAL has
    // one, and it saves before it lets go.
    "util.server": { run: () => A.runCmd?.("server"),
                     note: "saves the drawing and hands it back to the server, where every "
                           + "command is available again" },
    // ⚠ OPENDWG reads the drafter's OWN file. Its own handler for the same reason LOCAL has
    // one: there is nothing to select first.
    "util.opendwg": { run: () => A.runCmd?.("opendwg"),
                      note: "opens a .dwg or .dxf from this machine, read in the browser" },
    // ⚠ SAVEDXF writes it back. Same reason for its own handler, and the same reason it can only
    // run from a click or a typed command: a file picker outside a user gesture is refused.
    "util.savedxf": { run: () => A.runCmd?.("savedxf"),
                      note: "writes the drawing to this machine as DXF, from the browser" },
    // ⚠ and as DWG — the same file the drafter opened, written by `pdcad_core::todwg`
    "util.savedwg": { run: () => A.runCmd?.("savedwg"),
                      note: "writes the drawing to this machine as DWG, from the browser" },
    // ⚠ a drawing opened from disk has nowhere to go but that disk; this puts it in the list
    "util.upload": { run: () => A.runCmd?.("upload"),
                     note: "sends a drawing this tab holds to the server, as a NEW drawing" },
    "util.addselected": { note: WHY["util.addselected"] },

    // ── View, and the one camera ────────────────────────────────────────────────────────
    // ⚠⚠ **TYPING `ZOOM` NOW ASKS, AND CLICKING THE BUTTON STILL FITS.** They are two different
    // gestures and AutoCAD treats them differently too: the ribbon's Zoom Extents is one click
    // that means one thing, and the typed command is the start of a sentence. Making the button
    // ask would be a click that used to work needing two.
    "view.zoom.extents": { run: () => A.zoomFit?.(), typedRun: () => A.zoomPrompt?.() },
    "view.zoom.all": { note: "Zoom Extents does this here — the drawing has no separate limits" },
    "view.zoom.window": { ...tool("view.zoom.window"), note: "drag a window round what should "
                                                           + "fill the screen" },
    // ⚠ NOT greyed when the history is empty, even though AutoCAD greys its own. Grey in this
    // shell means one thing — "not built" — and the SketchUp lane's scar is what happens when it
    // starts meaning two. An empty history is said in words, the way AutoCAD says it at the
    // prompt: "No previous view saved."
    "view.zoom.prev": {
      run: () => {
        if (!A.canViewPrev?.()) { c.warn("No previous view saved."); return; }
        A.viewPrev?.();
      },
    },
    "view.zoom.realtime": { note: "the wheel zooms at the cursor" },
    "view.zoomgroup": { note: "the wheel zooms at the cursor" },
    "view.pan": { ...tool("view.pan"),
                  note: "drag to pan — the middle mouse button pans inside any command" },
    // ⚠⚠ **ITS GREY WAS A DESCRIPTION OF THE GAP, NOT A REASON.** "PAN drags the view; there is
    // no pan-by-two-points" is the specification for the thing that was missing — and two points
    // are not a slower drag, they are a MEASURED one: snap both to grid intersections and the
    // view shifts by exactly one bay. The same argument as MOVE taking a displacement.
    "view.pan.point": { ...tool("view.pan.point"),
                        note: "click a base point then a second point — the view shifts by "
                            + "exactly that displacement, snaps and all. Dragging moves it by "
                            + "however far your hand went" },
    // ── the camera ────────────────────────────────────────────────────────────────────────
    //
    // ⚠⚠ **TOP VIEW IS THE PLAN CANVAS, SO IT IS A RETURN AND NOT A CAMERA MOVE.** AutoCAD has
    // one model space; View ▸ 3D Views ▸ Top IS this drawing seen from above, and every other
    // standard view is the same model from somewhere else. `view.3d.top` therefore does not
    // call the 3D view's own `top` — it leaves the 3D view. Two "top views" that differed in
    // what you could draw on would be the worst of both.
    "view.orbit": { run: () => c.go3d(undefined, "iso"), on: cam3d,
                    note: "drag to orbit; View ▸ 3D Views ▸ Top comes back to the drawing" },
    "view.orbit.free": { note: "3DORBIT orbits freely; there is no constrained/free pair here" },
    "view.orbit.cont": { note: "there is no spin-and-let-go orbit" },
    // ⚠ Also not a sheet-set question — see `out.exportlayout`. VIEWBASE projects a 3D MODEL
    // onto a layout, and the blocker is the one SECTIONPLANE has: pdcad carries a 3D solid as
    // an opaque ACIS blob faithfully and cannot slice or project it.
    "view.base": {
      note: "not built — a base view projects a 3D SOLID onto a sheet, and pdcad stores solids "
            + "as opaque ACIS blobs it carries faithfully and cannot project. Cut a viewport "
            + "with MVIEW and set its scale",
    },
    "view.3dviews": { run: () => c.go3d(undefined, "iso"),
                      note: "Top is the drawing; the rest are the 3D view" },
    "view.3d.top": { run: () => c.setCamera("plan"), on: !cam3d,
                     note: "Top view IS the drawing canvas — this comes back to it" },
    ...Object.fromEntries(Object.entries(VIEW_OF).map(([id, v]) => [id, {
      run: () => c.go3d(undefined, v),
      on: cam3d && c.camera === v,
    }])),
    // ⚠⚠ **THE COMPASS CORNERS ARE PINNED TO THE ELEVATIONS, NOT TO A COMPASS.** The old note
    // here said these had to stay grey because the 3D view "does not name its compass corner",
    // and that a wrong corner is a mistake a drafter only catches on the printed sheet. The
    // second half is true; the first was not. Front, Back, Left and Right are already LIVE
    // above (`VIEW_OF`), so this shell has ALREADY committed to which side is which — and each
    // named isometric is simply the corner BETWEEN two of them:
    //
    //     SW = Front + Left     SE = Front + Right     NE = Back + Right     NW = Back + Left
    //
    // Nothing new is asserted, so there is no new way to be wrong: if the 3D lane ever settles
    // that its `front` (camera on +z) is really the back elevation, all eight views move
    // together and stay consistent, because these are derived from the same axes rather than
    // hard-coded to a bearing. Model3D's generic `iso` is +x+y+z — the SE corner.
    //
    // ⚠ No `on` tick. Which corner the camera is at is not in the reported tool state, and
    // putting the camera position there would re-render the whole shell on every orbit frame.
    ...Object.fromEntries(([["swiso", -1, 1], ["seiso", 1, 1], ["neiso", 1, -1],
                            ["nwiso", -1, -1]] as [string, number, number][])
      .map(([k, sx, sz]) => [`view.3d.${k}`, {
        run: () => c.go3d((a) => {
          const cam = a.camNow?.();
          if (!cam) return;
          const [tx, ty, tz] = cam.target;
          // keep the distance the user has zoomed to; only the direction changes
          const d = Math.hypot(cam.pos[0] - tx, cam.pos[1] - ty, cam.pos[2] - tz) || 1;
          const u = d / Math.sqrt(3);
          a.setCam?.({ pos: [tx + sx * u, ty + u, tz + sz * u], target: cam.target,
                       fov: cam.fov });
        }, "iso"),
        note: `-VIEW ▸ ${k.slice(0, 2).toUpperCase()} Isometric — the corner between the `
              + `${sz > 0 ? "Front" : "Back"} and ${sx > 0 ? "Right" : "Left"} elevations`,
      }])),
    "view.walk": { run: () => c.go3d((a) => a.walk(!c.m3d?.tool?.walk)),
                   on: !!c.m3d?.tool?.walk,
                   note: "walk through the model at eye height" },
    "view.visualstyles": { run: () => c.go3d((a) => a.style("shaded")),
                           note: "five of AutoCAD's ten are built — see the flyout" },
    ...vsHandlers,
    // ⚠⚠ **AN ANALYSIS PASS, NOT A SURFACE COMMAND.** Fifteen of the Surface tab's eighteen need
    // a surface record this app does not have. Draft Angle needs none: it colours faces of the
    // solids that already exist by how far they lean off the pull direction, which is the same
    // "the read-only members of a modelling tab are buildable" pattern INTERFERE followed.
    // ⚠ Real use here, not a curiosity: a moulded or cast part has to release, and a wall the
    // drawing says is vertical shows red the moment it is not.
    "surf.draft": { run: () => c.go3d((a) => a.style(c.m3d?.tool?.style === "draft"
                                                     ? "shaded" : "draft"), "iso"),
                    on: c.m3d?.tool?.style === "draft",
                    note: "ANALYSISDRAFTANGLE — colours every face by how far it leans off the "
                          + "vertical pull. Red is under 1°, which will not release; green is "
                          + "over 7°. Press again to come back to Realistic" },
    // ⚠⚠ **NEITHER OF THESE NEEDED A SHADER, WHICH IS WHAT THEIR NOTE ASSUMED.** It said "the
    // 3D view has five visual styles; Conceptual and Sketchy are two more shader passes it does
    // not have". Gooch is a colour ramp off the vertex normal — the same per-vertex machinery
    // the draft-angle and curvature analyses already use — and Sketchy is the EDGE geometry with
    // an overshoot. AFTER the `VS_OF` spread above, which gives them their run and lit state.
    "vs.conceptual": {
      ...vsHandlers["vs.conceptual"],
      note: "warm-to-cool shading instead of bright-to-dark, so a face turned away from the sun "
          + "reads as facing away rather than as unlit. ⚠ That matters indoors, where most of "
          + "the model never sees the sun",
    },
    "vs.sketchy": {
      ...vsHandlers["vs.sketchy"],
      note: "paper-white faces with hand-drawn edges that overshoot their corners. ⚠ The "
          + "overshoot is fixed in millimetres, not a percentage — a percentage makes a 6 m "
          + "wall overshoot 300 mm and a 40 mm reveal overshoot 2",
    },
    "vs.2dwire": { run: () => c.setCamera("plan"), on: !cam3d,
                   note: "2D Wireframe here is the drawing canvas itself" },
    // ⚠ NOT re-listed here after the VS_OF spread above — a key repeated in the same object
    // literal is won by the LAST one, which would grey Shaded with Edges again.
    // ── the UCS ───────────────────────────────────────────────────────────────────────────
    // ⚠ Our origin is `{x, y, rot}` — a 2D origin and a rotation, which IS AutoCAD's UCS in a
    // 2D drawing: "Specify origin" then "Specify point on X-axis". The third prompt (a point on
    // the XY plane) only exists in 3D and there is nothing to answer it with here.
    "ucs.set": { ...tool("ucs.set"),
                 note: "click the new origin, then point the X axis" },
    "ucs.world": { run: () => { toPlan(); A.resetAxes?.(); },
                   enabled: T?.ucsWorld === false,
                   note: T?.ucsWorld === false ? "puts the origin back at the drawing's own 0,0"
                                               : "the origin is already at the world 0,0" },
    // ⚠⚠ **"UCS WORLD RETURNS TO 0,0" IS NOT THE SAME OFFER AND THE NOTE TREATED IT AS ONE.**
    // A drafter who set the origin on a rotated wall to detail it wants the PREVIOUS origin
    // back, not the corner of the site. `PlanStudio` now keeps a 16-deep stack — a ref, since
    // nothing renders from it and making it state would re-render the canvas on every move.
    "ucs.previous": {
      run: () => { toPlan(); A.ucsPrev?.(); },
      enabled: !!T?.ucsCanPrev,
      note: T?.ucsCanPrev
        ? "steps the drawing origin back to where it was before the last move. ⚠ Not the "
          + "same as UCS World, which jumps all the way to 0,0"
        : "the origin has not been moved yet — there is nothing to step back to",
    },
    // ⚠⚠ **A UCS IS A VIEWING AID, SO IT IS STORED PER BROWSER — NOT ON THE DRAWING.** Where a
    // drafter is working from this afternoon is not a property of the building; putting it on
    // the plan would push one person's working origin into everybody else's copy and into the
    // DXF. The drawing's own axes already live in `localStorage` for that reason.
    "ucs.manager": {
      run: () => c.ucsManager(), arms: false,
      note: "saves the current drawing origin under a name and brings it back later. ⚠ Kept in "
          + "this browser, not on the drawing — a working origin is not part of the building",
    },
    "view.regen": { note: "the canvas redraws continuously; there is nothing to regenerate" },
    "view.regenall": { note: "the canvas redraws continuously; there is nothing to regenerate" },
    // ⚠ AutoCAD's named views and the 3D view's saved SCENES are the same idea under two names:
    // a remembered camera restored by name. Ours also keeps the visual style and the layer set,
    // which is MORE than AutoCAD's VIEW stores. The one place ours is narrower — AutoCAD saves a
    // 2D zoom window too — the dialog states rather than hides.
    "view.named": { run: () => c.openViews(),
                    note: "saved viewpoints of the model, by name" },
    // ⚠⚠ **THE ONLY MEASUREMENT IN THIS PROJECT TAKEN BY A PERSON.** Every benchmark here runs
    // on the box that serves the app, types at a fixed delay and never hesitates. This walks a
    // drafter through a scripted run on their own PC, over the LAN, with their own file, and
    // records what the app's own instrumentation says beside what they thought of it.
    "tools.testdrive": { run: () => c.openTestDrive(), arms: false,
                         note: "a timed run through the app, one instruction at a time — the "
                             + "app records how long each step took and what you thought of it" },
    // ⚠⚠ **ONE SPLIT, AND THE LIMIT IS THE SEAM RATHER THAN THE IDEA.** AutoCAD tiles model
    // space into 1, 2, 3 or 4 viewports. The plan canvas belongs to the studio PAGE, not to this
    // shell — a shell is handed `viewport` (which replaces the canvas outright) and `side` (a
    // flex sibling next to it) — so the drawing can appear exactly ONCE, and the only split that
    // can contain it is one vertical division with the drawing on the left. Three and Four Equal
    // would need the page to hand out N canvases, which is a change to the contract every shell
    // shares rather than a change to this one, and it is not worth making for a tiling.
    "view.vports": {
      run: () => c.setVports(c.vports === "2v" ? "" : "2v"),
      on: c.vports === "2v",
      note: c.vports === "2v"
        ? "back to one viewport"
        : "splits the model area in two: the drawing on the left, a live 3D view on the right. "
          + "The drawing stays the active viewport — it is the one you can draw in",
    },
    "view.vports.join": {
      ...(c.vports ? { run: () => c.setVports("") } : {}),
      note: c.vports ? "back to a single viewport"
                     : "there is only one viewport open",
    },
    "view.vports.restore": {
      ...(c.vports ? {} : { run: () => c.setVports(c.lastVports || "2v") }),
      note: c.vports ? "the last configuration is already restored"
                     : "puts back the split you last had",
    },
    "view.display": { note: "" },

    // ── Insert / Reference ──────────────────────────────────────────────────────────────
    // ⚠ AutoCAD's underlay FADE and our underlay OPACITY are the same control read from
    // opposite ends: more fade is less picture. The conversion lives here rather than in the
    // component, so the slider says what AutoCAD's says and stores what the studio's expects.
    "insert.underlay.fade": {
      slider: {
        value: Math.round(90 - (T?.underlay ?? 0.35) * 100),
        min: 0, max: 90, step: 5,
        onChange: (v: number) => A.setUnderlay?.((90 - v) / 100),
      },
      note: T?.hasUnderlay
        ? "how far back the underlay sits — 0 is full strength, 90 is nearly gone. ⚠ Group 282, "
          + "so it goes out in the DXF and a client sees the fade you chose"
        : "this drawing has no underlay to fade",
    },
    // ⚠⚠⚠ **THIS PARAGRAPH WAS THE DESIGN APP'S AND IT HAD THE SPLIT BACKWARDS FOR pdcad.** It
    // said fade, frame and monochrome are view settings because "a tracing aid belongs to the
    // person tracing". True of a plan photograph; FALSE of a DXF `PDFUNDERLAY`, which is an
    // object in a file a client opens. Groups 282 and 280 are entity data and go out with the
    // drawing. FRAME is the only one that really is a view setting — AutoCAD's own `FRAME`
    // variable — and it is the only one that stays in the studio's toggles.
    // ⚠ And all four sat GREY on `T.hasUnderlay`, which the studio never published: harmless
    // while pdcad had no underlay, and a LIE the moment PDFATTACH landed.
    "insert.uframe": {
      // ⚠ `uframe`, not `underlayFrame`: the studio publishes AutoCAD's own variable name, and
      // reading a field nobody writes is how all four of these came to be permanently grey.
      ...(T?.hasUnderlay ? { run: () => A.setUnderlayFrame?.(!T?.uframe) } : {}),
      on: !!T?.uframe,
      note: T?.hasUnderlay
        ? "AutoCAD's FRAME — outlines the underlay, so you can see where the survey stops. "
          + "⚠ This screen only: it is not written to the drawing, unlike fade and monochrome"
        : "this drawing has no underlay to frame",
    },
    "insert.adjust": {
      ...(T?.hasUnderlay ? { run: () => A.setUnderlayMono?.(!T?.underlayMono) } : {}),
      on: !!T?.underlayMono,
      note: T?.hasUnderlay
        ? "AutoCAD's Adjust ▸ Monochrome — desaturates the underlay and lifts its contrast, "
          + "which is what makes a wall line traceable at 35% fade. ⚠ Group 280, so it goes out "
          + "in the DXF. Fade itself is the slider here"
        : "this drawing has no underlay to adjust",
    },
    // ⚠⚠ **THE ONE UNDERLAY COMMAND THAT IS DRAWING DATA, AND IT NOW HAS A FIELD.** Fade and
    // Monochrome are per-viewer; a CROP is what everyone should see, so `DesignPlan.underlay_clip`
    // (migration 0035) holds `[x, y, w, h]` in world mm. The image is MASKED, never re-cut, so
    // removing the clip restores it exactly and nothing is lost.
    "insert.clip": {
      // ⚠ NO callback. The old one wrote `DesignPlan.underlay_clip` through the design app's
      // API, which pdcad has no equivalent of — so this was a lit button calling a dead route.
      // The clip is ENTITY data here: two picks, then one `underlay` edit.
      ...(T?.hasUnderlay ? { run: () => { toPlan(); A.startClip?.(); }, arms: true } : {}),
      note: T?.hasUnderlay
        ? "pick two corners; the underlay is clipped to that rectangle. ⚠ It goes out in the "
          + "DXF, so everyone who opens the drawing sees the same crop"
        : "this drawing has no underlay to clip",
    },
    "insert.clipdel": {
      // ⚠⚠ **IT RESETS THE CLIP TO THE WHOLE PAGE RATHER THAN DELETING IT.** "Clipped" and
      // "never clipped" are the same DXF state — no boundary path — and AutoCAD can delete it
      // because AutoCAD re-reads the PDF for the page size. This app kept a RENDER, not the
      // file. A boundary covering the whole page is the same picture and keeps the extent
      // knowable, so the button does the thing the drafter meant instead of explaining itself.
      ...(T?.hasUnderlay ? { run: () => A.clearUnderlayClip?.() } : {}),
      note: T?.hasUnderlay
        ? "shows the whole page again. ⚠ The clip is RESET to the page rather than deleted — "
          + "same picture, and this app keeps knowing how big the page is"
        : "this drawing has no underlay to unclip",
    },
    // ⚠⚠ **THE IMPORTER HAS ALWAYS WORKED; NOTHING COULD PRESS IT.** `importer.import_cad` reads
    // DWG and DXF, creates the layers it finds, follows XREFs one level and names the ones it
    // cannot resolve — and `/design/plans/<id>/import_cad/` has been serving it, with
    // `designImportCad` already written in `resources.ts`. The three buttons that mean "bring a
    // drawing in" were grey the whole time, one of them saying "DXF comes in on this tab", which
    // was true of the TAB and false of every button on it.
    // ⚠ Not `openImport`: that reads a DRAWING and copies its geometry in. This reads a
    // PICTURE and keeps its pixels with the drawing, which is a different record and a
    // different promise.
    "insert.imageattach": {
      run: () => A.attachImage?.(),
      arms: true,
      note: "pick a photo or scan, then drag the rectangle it fills. ⚠ The pixels are kept "
            + "WITH the drawing — a DXF only ever carries a file path, and measured on the 93 "
            + "image references stored here, not one of their files still exists",
    },
    // ⚠⚠⚠ **MEASURED BEFORE IT WAS BUILT, AND THE MEASUREMENT IS WHY THERE IS NO DWFATTACH.**
    // Across this office's 18,241 drawings: `PDFDEFINITION` in 742, `PDFUNDERLAY` in 207 (602
    // entities), `DWFUNDERLAY` in NONE. A W2D decoder would be a large piece of work for a
    // format nobody here sends — and unlike PDF there is no free reader to lean on.
    // ⚠ And the ones already in client files round-tripped perfectly all along and drew
    // NOTHING, because `display.build` had no branch for the entity. That half is fixed too.
    "insert.pdfattach": {
      run: () => A.attachPdf?.(),
      arms: true,
      note: "pick a PDF, then drag the rectangle its page fills — a scan to trace over. ⚠ The "
            + "frame keeps the PAGE'S shape rather than stretching to your drag, and the DXF "
            + "carries a real PDF underlay, so a client's AutoCAD resolves it at full "
            + "resolution against their own copy of the file",
    },
    "insert.dwfattach": {
      note: "DWF is not supported, and that is measured rather than assumed: of this office's "
            + "18,241 drawings, 207 carry a PDF underlay and NOT ONE carries a DWF. Attach the "
            + "PDF instead",
    },
    "insert.attach": {
      run: () => c.openImport(),
      // ⚠ Says what is different, because the difference is the whole meaning of XATTACH: an
      // AutoCAD xref stays a reference and re-reads its source when the source changes. Ours
      // copies the geometry in once. A drafter who assumes the AutoCAD behaviour would send the
      // consultant's revised grid and expect the drawing to follow it.
      note: "brings the file's geometry in ONCE — it does not stay linked, so a later revision "
            + "of the referenced file has to be imported again",
    },
    "insert.dwgref": {
      run: () => c.openImport(),
      note: "brings the file's geometry in ONCE — it does not stay linked, so a later revision "
            + "of the referenced file has to be imported again",
    },
    "insert.import": {
      run: () => c.openImport(),
      note: "reads DWG and DXF — geometry and layers, with xrefs followed one level. Nothing is "
            + "guessed to be a wall: it arrives as drawn shapes",
    },
    // ⚠⚠ **A PDF WAS ONLY EVER SOMETHING TO TRACE, AND MOST OF THEM ARE PLOTS.** The underlay
    // is the right tool for a SCAN; when a consultant sends a plotted PDF the lines are still
    // vectors in the file, and copying them by hand is transcribing something the computer can
    // read. `pdfimport.py` reads them; the dialog asks for the plot scale because a PDF carries
    // no units, and suggests the one printed in the title block.
    "insert.pdfimport": { run: () => c.openPdfImport(),
                          note: "PDFIMPORT — a PDF's own vectors as real geometry. A scan has "
                                + "none; place that as an underlay and trace it" },
    // ⚠ NOT its own command: PDF layers (optional content groups) are what the import GROUPS BY
    // when the file has them, reported in the dialog before you commit. A separate manager for
    // layers that only exist during an import would have nothing to manage afterwards — they
    // are this drawing's layers by then, in the Layer Properties Manager.
    "insert.pdflayers": { run: () => c.openPdfImport(),
                          note: "a PDF's own layers become this drawing's layers on import — "
                                + "the dialog lists them first. Afterwards they are in the "
                                + "Layer Properties Manager like any other" },
    "insert.datalink": { note: WHY["insert.datalink"] },
    // ⚠⚠⚠ **THE NOTE HERE WAS THE DESIGN APP'S, AND IT DESCRIBED A FEATURE THIS APP DOES NOT
    // HAVE.** It talked about `export.FIELDS`, `{{date}}`, `{{area}}` and `{{room}}` reading
    // "the room the note SITS IN" — all true of the app this chrome was lifted from, none of it
    // in pdcad. Third instance this week, after Pen Styles and GROUP.
    // ⚠ Measured instead: `FIELD` is in 28.9% of this office's drawings — 266,179 objects — and
    // of the 100 stored here, 33 are `\AcVar ctab` (a sheet name in a title block) and most of
    // the rest are AutoCAD's section/detail documentation, which this app does not model.
    "insert.field": {
      note: "a DXF field is an expression AutoCAD evaluates and caches into the note's text — "
          + "authoring one needs the field tree this app does not hold. UPDATEFIELD beside "
          + "this lists the ones your drawings already carry and says what each would read now",
    },
    "insert.updatefields": {
      run: () => { A.updateFields?.(); },
      note: "lists every field in this drawing, what it would read NOW, and which ones no "
          + "longer match their own text. ⚠ It REPORTS and does not rewrite: DXF stores the "
          + "evaluated result with nothing marking where in the string it sits, so replacing "
          + "\"SHEET: A-101\" with \"A-101\" would delete the label",
    },

    // ── palettes ────────────────────────────────────────────────────────────────────────
    "pal.toolpalettes": pal("pal.toolpalettes"),
    "pal.blocks": pal("pal.blocks"),
    "pal.history": { ...pal("pal.history"),
                     note: "the drawing's saved versions — geometry only, not sheets" },
    // ⚠⚠ **BUILT — AND THE NOTE ABOVE SAID IT NEEDED "cross-drawing block search, which does
    // not exist yet".** It exists: the cross-drawing clipboard's definition carry is reached by
    // NAME through `/block_def/`, and `_blocks_named_by` is one function rather than two copies
    // of the same nesting walk. A block this drawing already has is KEPT, never redefined.
    "pal.designcenter": {
      run: () => A.designCenter?.(),
      note: "browse another drawing's blocks and place one here. It comes with any block IT "
          + "uses. ⚠ A block this drawing already has is kept as it is — placing one from "
          + "elsewhere never redefines the copies already on your plan",
    },
    "pal.sheetset": { note: ON_SHEET },
    "pal.xref": { run: () => A.xrefs?.(),
                  note: "external references — a path stored on a machine that is not this one "
                        + "usually will not resolve, so BIND is what this offers" },
    "pal.markup": { note: WHY["pal.markup"] },

    // ── layout tabs ─────────────────────────────────────────────────────────────────────
    // ── model space and the layouts ───────────────────────────────────────────────────────
    // ⚠ One handler per page the drawing actually has. There is no `layout.1` / `layout.2`:
    // those were a screenshot of AutoCAD rather than what AutoCAD does, and they greyed with a
    // note pointing at the sheet composer while a drawing with real sheets sat right there.
    "layout.model": { run: () => c.setSpace(null), on: c.space === null,
                      note: "the drawing itself — where you draw" },
    ...Object.fromEntries(c.pages.map((pg) => [`layout.page.${pg.id}`, {
      run: () => c.setSpace(pg.id),
      on: c.space === pg.id,
      note: "a composed sheet — drag its viewports here, plot it from PLOT ▸ Publish",
    }])),

    // ── paper space: LAYOUT and MVIEW ─────────────────────────────────────────────────────
    // ⚠ `A.newLayout` and friends are the studio's, not the chrome's: a layout is a row in the
    // drawing's own database and this file has never known how to write one. See `PAPER_CMDS`.
    "layout.new": { run: () => A.newLayout?.() },
    "layout.rename": { run: () => A.renameLayout?.() },
    "layout.delete": { run: () => A.deleteLayout?.() },
    // ⚠ MVIEW is in `MODE_OF` — a two-point drag, armed the way LINE and RECT are.
    "layout.vpscale": { run: () => A.vpScale?.() },
    // ⚠ A TOGGLE with its state on it, not a command that always locks. AutoCAD's Properties
    // palette shows Display locked as Yes/No and this is the same control; a button that only
    // locks would leave the drafter hunting for the way back.
    "layout.vplock": T?.vpLock === undefined
      ? { note: "select one viewport on a sheet — the lock belongs to a window, not to a sheet" }
      : { run: () => A.vpLock?.(!T.vpLock), on: !!T.vpLock,
          note: T.vpLock
            ? "this window's scale is locked — press to unlock it. VPSCALE refuses a locked "
              + "window rather than quietly clearing the flag"
            : "locks this window's plot scale, so a stray zoom cannot change the sheet" },
    "layout.vplayer": T?.vpLock === undefined
      ? { note: "select one viewport on a sheet, then use the ▣❄ column in the Layers palette" }
      : { note: "the ▣❄ column in the Layers palette freezes a layer in THIS window only — "
              + "❄ next to it freezes it in the whole drawing, which is a different command" },

    // ── the running object snaps ────────────────────────────────────────────────────────
    // ⚠⚠ **BUILT FROM `OSNAP_MODES`, NOT FROM A SECOND LIST BESIDE IT.** This was a hand-written
    // pairing of six ids to six studio snap names, sitting a file away from the catalog's own
    // `snap:` column that says exactly the same thing — so the two could disagree, and they did:
    // the catalog greyed `quadrant`, which `objectSnap` has computed since the day it was
    // written. One table now, and the drop-up, the tooltip and the handler cannot drift apart.
    //
    // ⚠ All fourteen are live as of 2026-08-31. Node was the last one out: its grey said
    // "POINT is not built", POINT was built the same day, and the reason expired with it. The
    // fallback branch below is kept for a snap added to `OSNAP_MODES` without an engine.
    ...Object.fromEntries(OSNAP_MODES.map((m) => [m.id, m.snap
      ? { run: () => A.toggleSnap?.(m.snap), on: snaps.includes(m.snap) }
      : { note: SOON }])),
    "osnap.settings": { note: "tick the snaps in this list — there is no separate dialog" },

    // ── the status bar ──────────────────────────────────────────────────────────────────
    // ⚠ A master OSNAP toggle built out of the only lever we have: turn every running snap off,
    // and put back exactly the ones that were on. F3 is the toggle every drafter reaches for
    // when a snap is fighting them, so it is worth building from parts rather than greying.
    "st.osnap": {
      run: () => {
        if (snaps.length) snaps.forEach((k) => A.toggleSnap?.(k));
        else ["endpoint", "midpoint", "intersection"].forEach((k) => A.toggleSnap?.(k));
      },
      on: snaps.length > 0,
      note: snaps.length ? "turns every running snap off" : "puts the three usual snaps back",
    },
    // Bulleted rather than clickable: these are true of the canvas and there is nothing to set.
    // ⚠ DYNIN, LWT and ISOLATE were three of the "always on / not built yet" greys. All three
    // described something real that had no switch; all three have one now.
    "st.dynin": { run: () => A.setDynin?.(!T?.dynin), on: !!T?.dynin,
                  note: "F12 — the bar over the drawing carries the prompt and the Distance and "
                        + "Angle boxes. Off, the command line below shows the same prompt" },
    "st.lwt": { run: () => A.setLwt?.(!T?.lwt), on: !!T?.lwt,
                note: "draw pen weights, or every line at a hairline — which is how you see "
                      + "whether two lines really are two" },
    // ⚠⚠ **A TOGGLE THAT DOES TWO THINGS, because AutoCAD has two commands and one button.**
    // Pressing it with something selected ISOLATES that; pressing it again UNISOLATES. Wiring
    // only ISOLATEOBJECTS would leave a drafter looking at three walls with no way back — the
    // one state where a missing command is not merely absent but trapping.
    "st.isolate": {
      run: () => (T?.isolated ? A.unisolate?.() : A.isolate?.()),
      on: !!T?.isolated,
      note: "shows only what is selected. Press it again to bring everything back",
    },
    // ⚠⚠ **IT SAID `on: true` AND HAD NO `run`** — a toggle drawn permanently lit that did
    // nothing, describing a behaviour that did not exist. Cycling itself DID: TAB has walked
    // the stack under the cursor since the cycle state was added. What was missing is the
    // mouse-only half this toggle names, and AutoCAD ships it OFF, because with it on a second
    // click on a stack selects the second item — surprising if you did not ask for it.
    "st.cycling": { run: () => A.setCycling(!T?.cycling), on: !!T?.cycling,
                    note: T?.cycling
                      ? "clicking the same point again steps through what is stacked there — "
                        + "the count shows as you go. TAB does the same from the keyboard"
                      : "off: a click always takes the topmost. TAB still steps through the "
                        + "stack under the cursor" },
    "st.units": { on: true, note: "the drawing is in millimetres" },
    // ⚠⚠ **AutoCAD'S BLACK MODEL SPACE, WHICH THIS CANVAS HAS ALWAYS HAD AND THIS SHELL COULD
    // NOT REACH.** Options ▸ Display ▸ Color theme flips the CHROME; the canvas stayed paper
    // white whatever you chose, so a drafter who came from AutoCAD saw white and had no setting
    // to change. The dark canvas is not new — `viewPref.dark` has drawn it since the Rayon lane
    // shipped Shift+D, `T.dark` reports it and `A.setViewPref` writes it. Only this shell was
    // never wired to either.
    // ⚠ DEFAULT STAYS WHITE. It is a shared preference (`pderp.design.view`, not per drawing),
    // so flipping the default would repaint the other two lanes' canvases for everyone.
    // ⚠ AutoCAD keeps this in Options ▸ Display ▸ Colors, so that is where it goes — not on a
    // ribbon panel it has never had one on.
    "ui.canvasbg": {
      run: () => A.setViewPref({ dark: !T?.dark }),
      on: !!T?.dark,
      note: T?.dark
        ? "model space is black, the way AutoCAD ships — geometry inverts to white, paper "
          + "and plot are unaffected"
        : "model space is paper white. Tick for AutoCAD's black background",
    },
    // ⚠⚠ **THE FOUR DRAFTING F-KEYS ARE REAL AS OF 2026-08-31.** All four were grey, and three
    // of them carried notes describing a behaviour that DID exist and could not be switched
    // off — "grid snap is always on", "hold Shift to lock to an axis", "there is no grid to
    // display" (there was; it had been drawn since the canvas was written). A note that
    // explains why you cannot turn something off is not a reason for a grey button, it is a
    // description of the missing feature.
    // ⚠ F7/F8/F9/F10 are bound on the CANVAS, not here, so they work in every interface — a
    // drafter's fingers do not know which shell is on. See `PlanStudio`'s key handler.
    "st.grid": { run: () => A.setShowGrid?.(!T?.showGrid), on: !!T?.showGrid,
                 note: "F7 — draw the grid, or a plain sheet" },
    "st.snapmode": { run: () => A.setSnapMode?.(!T?.snapMode), on: !!T?.snapMode,
                     note: "F9 — round every pick to the grid. Ctrl still escapes it for one "
                           + "click while it is on" },
    "st.ortho": { run: () => A.setOrtho?.(!T?.ortho), on: !!T?.ortho,
                  note: "F8 — lock every gesture to horizontal or vertical" },
    "st.polar": { run: () => A.setPolar?.(!T?.polar), on: !!T?.polar,
                  note: `F10 — lock to the nearest ${T?.polarAng ?? 15}°. `
                        + "Pick the angle on this button's drop-up" },
    // ⚠ AutoCAD's own POLARANG list, and the drop-up SETS it rather than listing it for show.
    ...Object.fromEntries(POLAR_ANGLES.map((a) => [`polarang.${a}`, {
      run: () => { A.setPolarAng?.(a); A.setPolar?.(true); },
      on: (T?.polarAng ?? 15) === a,
    } as AcadHandler])),
    // ⚠ Object snap TRACKING is not object snap: it draws alignment rays from points you have
    // hovered, so you can pick the intersection of two things neither of which is there. It
    // needs an acquired-point store the canvas does not have; the snaps themselves are on F3.
    // ⚠ Built 2026-09-01. Its old note said "nothing here remembers a hovered point yet" —
    // which named exactly what had to exist, and then it did. Same shape as `osnap.node`.
    "st.otrack": { run: () => A.setOtrack?.(!T?.otrack), on: !!T?.otrack,
                   note: "F11 — hover a point to acquire it, then align to its horizontal and "
                         + "vertical rays. Two acquired points give you their crossing, which "
                         + "is a place nothing exists" },
    // ⚠⚠ **THIS REASON WENT STALE THE MOMENT THE PARAMETRIC TAB WAS WIRED.** It said
    // "constraints are not built — the whole Parametric tab is grey", and ten of them are live.
    // What is still true is the part that matters: INFER creates PERSISTENT constraints as you
    // draw, and ours apply once and leave nothing behind, so there is nothing to infer INTO.
    // ⚠⚠ **THE OLD NOTE NAMED EXACTLY WHAT WAS MISSING, SO THAT IS WHAT GOT BUILT.** It said the
    // ten geometric commands "apply once and leave nothing behind, so there is no constraint for
    // this to add to — that is the half that is missing, not the constraints themselves".
    // `DesignConstraint.GEOMETRIC` is that half: five kinds that PERSIST and carry no value.
    //
    // ⚠ `horizontal` on the Parametric tab is a horizontal DISTANCE — it holds a point so many
    // millimetres along x and keeps its y. `g_horiz` holds the y EQUAL. Two different things
    // with one name, and confusing them is why this could not be built on what was there.
    //
    // ⚠ Level and plumb ONLY, and the note says so. AutoCAD also infers coincident, tangent and
    // perpendicular by watching which object snap produced each pick; this canvas does not
    // record that, so inferring them would mean guessing from the result — and a constraint
    // nobody asked for moves geometry on the next solve.
    "st.infer": {
      run: () => A.setInferCon?.(!c.T?.inferCon), on: !!c.T?.inferCon,
      note: "while this is on, a line drawn within 1° of level or plumb is straightened and "
          + "KEEPS a constraint saying so — drag it later and it springs back. ⚠ Level and "
          + "plumb only; the other eight are on the Parametric tab and apply once",
    },
    // ⚠⚠ **NOT A 30° POLAR STEP, WHICH IS WHY IT IS ITS OWN MODE.** An isoplane offers exactly
    // three directions — two axes and the vertical — and which two depends on the face. A 30°
    // polar increment also offers 60°, 120° and 150°, which are not isometric axes at all, so a
    // drafter would draw a line that looks right and sits on no face.
    "st.iso": {
      run: () => A.setIso?.(!T?.iso),
      on: !!T?.iso,
      note: T?.iso
        ? `isoplane ${T?.isoName} — F5 cycles Left, Top, Right`
        : "locks the cursor to the isometric axes so a 2D plan can be drawn as an isometric. "
          + "F5 cycles the isoplane once it is on",
    },

    // ⚠ AutoCAD's TRANSPARENCYDISPLAY. A VIEW setting: it changes nothing on any object, which
    // is what makes it safe to press while checking that nothing is hidden under a faded hatch.
    "st.transparency": {
      run: () => A.setShowAlpha?.(!T?.showAlpha), on: !!T?.showAlpha,
      note: T?.showAlpha
        ? "transparency is being DRAWN. Turn it off to see every object solid — nothing is "
          + "changed, so anything hiding under a faded hatch shows up"
        : "transparency is being IGNORED — every object draws solid. The objects themselves "
          + "still carry their transparency and still plot with it",
    },
    "st.annovis": { note: WHY["st.annovis"] },
    "st.autoscale": { note: WHY["st.autoscale"] },
    "st.annoscale": { note: ON_SHEET },
    // ⚠ The annotation-scale drop-up lists AutoCAD's own scales and every one is grey, because
    // a drawing's plot scale here belongs to the SHEET it is placed on, not to model space.
    ...["1_1", "1_2", "1_5", "1_10", "1_20", "1_50", "1_100"]
      .reduce((o, k) => { o[`annoscale.${k}`] = { note: ON_SHEET }; return o; },
              {} as Record<string, AcadHandler>),
    "st.annomon": { note: WHY["st.annomon"] },
    "st.qp": { note: "the Properties palette shows what is selected" },
    "st.lockui": { note: WHY["st.lockui"] },

    "st.gfx": { note: WHY["st.gfx"] },
    // ⚠⚠ **ALL THREE WORKSPACES ARE REAL AS OF 2026-08-31.** `ws.3dmodel` used to carry the note
    // "this shell builds the Drafting & Annotation workspace only", and the reasoning beside it
    // was that Solid, Surface and Mesh hold commands this product does not have. That was true
    // and it was the wrong conclusion: this shell's whole method is to show AutoCAD's real
    // structure and grey what is not built, with a reason — which is more useful than an absent
    // tab, because a grey button teaches "not yet" and a missing one teaches nothing. The three
    // tabs are also not empty: UNION, SUBTRACT and INTERSECT are the studio's own boolean solid
    // ops, SECTIONPLANE is its section tool, and Visualize is the 3D view's cameras and styles.
    //
    // ⚠ Switching resets the open TAB as well, because a workspace that leaves you on a tab it
    // does not contain is the one way this can look broken — see `AcadRibbon`'s fallback, which
    // catches it in the render but leaves the strip looking as though nothing is selected.
    ...Object.fromEntries(["ws.drafting", "ws.3dbasics", "ws.3dmodel"].map((id) => [id, {
      run: () => c.setCfg({ ws: id, tab: "home" }),
      on: c.ws === id,
    } as AcadHandler])),
    "st.workspace": { on: true,
                      note: WS_NAME[c.ws] + " — switch on the status bar's workspace drop-up" },
    "ws.saveas": { note: "a workspace here is its ribbon tabs, and those are fixed per workspace "
                         + "— there is nothing of your own in one to save" },
    "ws.settings": { note: WHY["ws.settings"] },

    // ── Parametric, Manage, Express — three whole tabs, and each is unbuilt for its OWN
    //     reason, which is worth saying rather than repeating "not built yet" 44 times ──────
    //
    // ⚠ These stay grouped because within each tab the reason really IS the same one: a
    // constraint solver, an extension system, a bag of legacy utilities. Grouping where the
    // reason is shared is honest; grouping where it is not is how "Aligned, Angular, Radius and
    // Diameter are built" ended up on eight different dimension commands that needed eight
    // different things.
    // ⚠⚠ **PARAMETRIC IS NO LONGER HERE — IT IS WIRED, AND THIS BLOCK USED TO SILENTLY WIN.**
    // The nineteen `par.*` ids were greyed by a `.reduce` right here, which is LATER in this
    // object literal than the handlers that now build them, and a later key wins. So the ten
    // live constraints were built, the memo rebuilt, tsc passed, and every button on the tab
    // still read "needs a solver" — a wiring change that looked exactly like no change at all.
    // ⚠ It is also why `par.*` did not show up in the fall-through census: this file greys ids
    // through TEMPLATE LITERALS, which no grep for `"par.foo":` will ever find. `man.*` and
    // `exp.*` below are the same shape. Count what the BROWSER reports, not what the source
    // looks like.
    ...["record", "play", "usermsg", "pref", "cui", "editaliases", "tp", "loadapp", "runscript",
        "vlisp", "vba", "configstd", "checkstd", "layertrans"]
      .reduce((o, k) => { o[`man.${k}`] = { note:
        "the Manage tab customises AutoCAD — macros, LISP, VBA, its own menus and aliases. This "
        + "app is not extensible from outside and its aliases are the acad.pgp set built in, so "
        + "there is nothing here to record, load or configure" }; return o; },
              {} as Record<string, AcadHandler>),
    // ⚠⚠ **FOUR OF THESE ELEVEN WERE ALREADY BUILT UNDER ANOTHER NAME.** Express Tools is
    // Autodesk's bag of odd jobs, and the blanket reason below is right about most of it — but
    // OVERKILL is `mod.overkill`, which has been live since the day it was written, and QQUI is
    // QSELECT, which the Home tab opens. Greying a command that the same handler table lights up
    // three panels away is the "live in one place, dead in another" fault, which is worse than a
    // consistent grey because a drafter concludes the FEATURE is missing.
    "exp.overkill": { ...op("mod.overkill"),
                      note: "the same command as Modify ▸ Delete Duplicate Objects — removes "
                            + "shapes that sit exactly on top of one another" },
    // ⚠ NOT wired, and it nearly was. The catalog labelled this "Quick Select" while giving it
    // the command QQUIT, so it went on the list of already-built-elsewhere with OVERKILL and
    // XPLODE. QQUIT closes every open drawing without saving. One drawing is open here and it
    // saves itself, so the command has nothing to do — and Quick Select, the thing the label
    // promised, is live on the Home tab under its own name.
    "exp.qqui": { note: "QQUIT closes every open drawing. One is open here and it saves itself "
                        + "as you draw. Quick Select — which this button used to be mislabelled "
                        + "as — is on Home ▸ Utilities" },
    "exp.explode": { ...op("mod.explode"),
                     note: "the same command as Modify ▸ Explode. AutoCAD's EXTENDED explode "
                           + "differs only in keeping colour and layer through a nested block, "
                           + "and a block here has no nested entities to lose them" },
    "exp.txt2mtxt": {
      run: () => {
        const r = A.joinText?.();
        if (!r) return;
        if (r.why) { c.warn(r.why); return; }
        c.warn(`${r.n} notes joined into one.`);
      },
      note: "joins the selected notes into one multi-line note, in the order they read on the "
            + "sheet — top to bottom, then left to right — not in the order they were typed",
    },
    // ⚠ The seven that are left, each with its OWN reason. The blanket one they shared said
    // "FLATTEN wants a 3D drawing, SAVEALL wants unsaved files, and neither exists here" —
    // true of those two and a shrug at the other five, which is how BREAK-LINE (a symbol this
    // office draws on every shop drawing) sat behind a sentence about file saving.
    "exp.flatten": { note: "flattens a 3D drawing onto one plane. This canvas IS one plane — "
                           + "the 3D model is generated from it, never drawn into it" },
    "exp.saveall": { note: "saves every open drawing. One drawing is open at a time here, and "
                           + "it saves itself as you draw" },
    // ⚠ The note ruled out blocks and xrefs and never mentioned the container this app DOES
    // have. A design group is enterable — double-click into it, pick the member, copy — which
    // is NCOPY's whole job done by a gesture that already exists. A grey that lists what it
    // cannot do and omits what the drafter actually wants sends them away for nothing.
    "exp.ncopy": { note: "copies an entity out of a container without exploding it. A block "
                         + "here is a catalogue SYMBOL — one drawing, no sub-entities to reach "
                         + "into — and an import becomes this drawing\u2019s own shapes rather "
                         + "than staying a reference. \u26a0 For a GROUP the gesture exists: "
                         + "double-click into it, pick the member, Copy" },
    "exp.copym": { note: "COPY, then repeat at Divide/Measure/Array spacings. All four of those "
                         + "are separate commands on the Modify panel and every one is built — "
                         + "this is their menu, not a capability" },
    "exp.txtexp": { note: "turns text into outlines so it can be filled or trimmed. That needs "
                          + "the font\u2019s glyph curves; this canvas draws notes as SVG text and "
                          + "the PDF embeds DejaVu, so no outline is ever computed" },
    "exp.arctext": { note: "sets text along an arc. A note here has one anchor and one rotation, "
                           + "so curving it means storing a path per note — and a trilingual "
                           + "drawing would need it to work for Vietnamese diacritics and 中文 too" },
    // ⚠⚠ **BUILT BECAUSE THE PREVIOUS NOTE SAID IT SHOULD BE.** That grey read "Not built, and
    // it SHOULD be — every shop drawing this office issues uses one. It is a polyline with a
    // zig-zag, so nothing new is needed except the tool that places it." Writing the job down
    // instead of a verdict is what made it a twenty-minute change rather than a decision.
    "exp.breakline": { ...tool("exp.breakline"),
                       note: "two clicks give the run; the Z is drawn at its middle. It lands on "
                             + "A-ANNO-TEXT, not the drawing layer — a break symbol is a note "
                             + "ABOUT the drawing and must not reach a takeoff" },
    // ── Solid, Surface, Mesh and Visualize — the 3D tabs ────────────────────────────────
    //
    // ⚠⚠ These shipped earlier today with no handler entries at all, so the default `{}` gave
    // sixty buttons a tooltip saying "not built yet". That is the failure the tabs were added
    // to AVOID: the whole argument for showing AutoCAD's real structure is that a grey button
    // teaches "not yet, and here is why" — sixty identical "not built yet"s teach exactly as
    // much as sixty missing buttons, which is nothing.
    ...Object.fromEntries(Object.entries(SOLID_WHY).map(([k, v]) => [k, { note: v }])),
    // ⚠⚠⚠ **THESE FOUR DOCUMENTED THE DESIGN APP AND WEDGED THIS ONE.** The notes described
    // `kernel.faces` addressing a face by its centroid, and `face_offset_wire` shipping "hours
    // before the button did" — all true, of the DESIGN app at /opt/dsn, which has an OCCT
    // kernel. pdcad has no `kernel.py`, no `face_offset_wire` and no modelling kernel at all;
    // `SOLID_WHY` two spreads up says so in its own words, and these entries were written AFTER
    // it specifically to win over it.
    //
    // ⚠⚠ **AND OFFSETEDGE TOOK THE WHOLE APP DOWN.** `c.solidFaceOp` calls `go3d`, which swaps
    // the 2D canvas for the 3D viewport, then opens a NATIVE `window.prompt`, then posts to
    // `designSolidOpAdd` — a stub that rejects. The command survey found it: everything typed
    // after OFFSETEDGE read as silent until the page was reloaded, which tarred the 130
    // commands that follow it alphabetically and froze the built count at 134.
    //
    // ⇒ Refused by name, with the reason, like every other command this app cannot do. The
    // measured note in `SOLID_WHY` was right the first time; these four overrode it with
    // another application's changelog.
    // ⚠ Written out rather than read from `SOLID_WHY`: only `sol.offsetedge` is a key there,
    // so mapping all four through it would have given three buttons `note: undefined` — a
    // tooltip-less grey button, which is the "sixty identical not-built-yets" failure the note
    // above this block warns about, arrived at from the other direction.
    ...Object.fromEntries(([
      ["sol.shell", "hollows a solid to a wall thickness. pdcad is 2D and has no modelling "
                  + "kernel — it carries a solid's ACIS payload through a round trip untouched "
                  + "and cannot change its shape"],
      ["sol.slice", "cuts a solid with a plane. pdcad is 2D and has no modelling kernel; SLICE "
                  + "on a 2D drawing is TRIM, which is on the Home tab and built"],
      ["sol.imprint", "marks one solid's edges onto another's face. pdcad is 2D and has no "
                    + "modelling kernel — there is no face to imprint onto"],
      ["sol.offsetedge", SOLID_WHY["sol.offsetedge"]],
    ] as [string, string][]).map(([k, v]) => [k, { note: v } as AcadHandler])),
    "sol.thicken": {
      run: () => c.makeSurface("__thicken"), arms: false,
      note: "gives a SURFACE a material thickness and makes it a solid. Select a skin from the "
          + "Surface tab first — this is the deliberate step where 12 mm MDF or 1.2 mm folded "
          + "steel gets decided",
    },
    // ── the Surface tab ────────────────────────────────────────────────────────────────
    //
    // ⚠⚠ **SEVENTEEN GREYS BEHIND ONE PARAGRAPH, AND THE PARAGRAPH WAS RIGHT.** It said every
    // body in this model is a SOLID derived from a record, and a NURBS surface is a skin with
    // no thickness. Both true — and the answer was the same one `DesignSolid` was: give the
    // skin a record of its own. `DesignSurface` is that record.
    //
    // ⚠ A surface has NO INSIDE. It cannot cut and cannot be cut; SCULPT and THICKEN are how
    // one becomes something a boolean can touch, and both make a real solid first.
    ...Object.fromEntries((
      [["planar", "a flat skin inside a closed curve. ⚠ The points must BE flat — Patch is the "
                  + "one that does not care"],
       ["extrude", "sweeps the selected curve straight up into a skin — a shopfront fascia, a "
                   + "bulkhead face. Not a prism: it has no thickness"],
       ["revolve", "spins the selected curve about the drawing's vertical axis"],
       ["loft", "a skin through two or more curves, in the order you selected them"],
       ["sweep", "drives the first selected curve along the second"],
       ["offset", "a parallel skin at a fixed distance from this one"],
       ["patch", "fills a closed boundary that does NOT have to be flat — the answer when "
                 + "Planar refuses"],
       ["network", "fills a boundary through cross curves. Same filler as Patch, more control"],
       ["blend", "a tangent skin between two surface edges"],
       ["fillet", "a rounded skin between two surfaces"],
       ["extend", "stretches a surface past its edge"],
      ] as [string, string][]).map(([k, note]) => [`surf.${k}`, {
        run: () => c.makeSurface(k), arms: false,
        note: `${note}. ⚠ A skin with no thickness — THICKEN gives it material`,
      }])),
    // ⚠⚠ **A PROJECTED CURVE IS A MARKING ON A SKIN, NOT AN OBJECT.** Giving it a record of its
    // own would create a second thing that has to follow the surface when the surface moves, and
    // the first time they disagreed nobody could say which was right. It is re-projected on
    // every read, so moving the plan curve moves the marking.
    "surf.project": {
      run: () => c.makeSurface("__project"), arms: false,
      note: "drops the selected plan curve straight down onto the surfaces below it and draws it "
          + "on them. ⚠ Straight down only — this canvas is one plane, so that is the one "
          + "direction it can mean. A curve that lands on nothing says so",
    },
    // ⚠⚠ **TRIM STORES THE OPERATION AND KEEPS EVERY PIECE.** That is the whole reason UNTRIM
    // can exist at all: baking the survivor would leave it a button that could only apologise.
    "surf.trim": {
      run: () => c.makeSurface("__trim"), arms: false,
      note: "cuts the first surface with the others and keeps the largest piece. ⚠ Nothing is "
          + "destroyed — the cut is stored and Untrim takes it back. Surfaces that do not cross "
          + "are refused rather than silently left alone",
    },
    "surf.untrim": {
      run: () => c.makeSurface("__untrim"), arms: false,
      note: "restores a trimmed surface whole. ⚠ Possible only because Trim stores the cut "
          + "instead of baking the survivor",
    },
    "surf.sculpt": {
      run: () => c.makeSurface("__sculpt"), arms: false,
      note: "sews a watertight set of surfaces into a SOLID. ⚠ The one place a skin becomes a "
          + "volume without anybody choosing a thickness — the surfaces already enclose the "
          + "space. It refuses an open set rather than making a hollow lump",
    },
    // ⚠ These two stay grey and their reasons are unchanged and still true: both are per-pixel
    // shading over a surface, not geometry, and this viewport has no fragment stage to hang
    // them on. Building surfaces did not make them possible.
    // ⚠⚠ **ITS REASON WENT STALE THE MOMENT SURFACES EXISTED.** It read "this model is walls,
    // slabs and box furniture" — nothing curved to analyse — which was true right up until the
    // Surface tab started making skins that bend.
    //
    // ⚠ A DISCRETE estimate off the tessellation, not analytic curvature off the NURBS surface,
    // and the note says so. Claiming the second for the first would be a number somebody could
    // quote at a fabricator.
    "surf.curvature": {
      run: () => c.go3d((a) => a.style(c.m3d?.tool?.style === "curve" ? "shaded" : "curve"),
                        "iso"),
      on: c.m3d?.tool?.style === "curve",
      note: "colours every surface by how sharply it bends — blue flat, red where it turns "
          + "hardest. ⚠ Read off the MESH, not the exact surface, and scaled against this "
          + "object's own peak. Press again to come back",
    },
    // ⚠⚠ **ITS NOTE WAS RIGHT ABOUT THE HARD PART AND WRONG ABOUT THE CONCLUSION.** It said the
    // stripes "need a PER-PIXEL shader — they are a reflection, so they have to be computed at
    // the fragment, not at the vertex". Both true. What it missed is that this viewer ALREADY
    // patches the fragment stage: `patchForPhoto` carries the world position and world normal
    // there for the photo projector, which is exactly what zebra needs. It rides that patch
    // rather than adding a second `onBeforeCompile` to the same material.
    "surf.zebra": {
      run: () => c.go3d((a) => a.style(c.m3d?.tool?.style === "zebra" ? "shaded" : "zebra"),
                        "iso"),
      on: c.m3d?.tool?.style === "zebra",
      note: "the reflection of a striped light, the way a paint shop checks a panel: a kink in "
          + "the surface shows as a STEP in the stripe. ⚠ Orbit while it is on — the reading is "
          + "how the bands move, not how they look from one angle",
    },
    // ── Mesh ───────────────────────────────────────────────────────────────────────────
    //
    // ⚠⚠ **CONVTOSOLID IS THE ONE MESH COMMAND THAT MATTERS HERE, AND IT MATTERED A LOT.**
    // `solidops` refuses triangles — "a boolean needs a B-rep" — so a designer could boolean
    // everything this app generates and nothing a client sends them. The other eleven on this
    // tab are subdivision editing and stay grey: the meshes here are OUTPUT.
    "mesh.tosolid": {
      run: () => c.meshToSolid(), arms: false,
      note: "sews an imported mesh into an exact solid, so a client's .skp furniture can be cut "
          + "by a wall. ⚠ It must be watertight — a mesh with a hole is refused rather than "
          + "sewn into an open shell that would boolean wrongly",
    },
    "mesh.tosurface": {
      run: () => c.meshToSolid(), arms: false,
      note: "same conversion as Convert to Solid — this app has one B-rep, and a watertight "
          + "mesh becomes a solid rather than a loose skin",
    },
    // ── Visualize: lights you PLACE ────────────────────────────────────────────────────
    //
    // ⚠⚠ **FOR THIS COMPANY THE LIGHTING IS PART OF THE PRODUCT.** A cosmetics fit-out is sold
    // on how the goods look under the cove light over the gondola; the office keeps four
    // LIGHTING layers and a `P6.0 REFLECTED CEILING PLAN` in its standard sheet set. The old
    // note said "a lighting model the 3D viewport does not have" — it has a hemisphere, a sun
    // and a camera lamp, so what it lacked was lights you can PUT somewhere.
    //
    // ⚠ `intensity` is the illuminance at ONE METRE. The scene is millimetres and three.js
    // divides a decaying light by the square of the distance in scene units — see `DesignLight`.
    // ⚠⚠⚠ **FOUR BUTTONS THAT LOOKED LIVE AND COULD NOT WORK, WITH NOTES THAT DESCRIBED THE
    // RESULT.** Each had a `run` calling `c.placeLight(...)` → `designAddLight` → `soon("LIGHT")`,
    // a REJECTING stub, so every click ended in a raw endpoint error. And the notes said what
    // would have happened — "Placed at the selection, 2700 mm up" — which is the worst kind of
    // wrong: a tooltip that reads as a measured fact about behaviour that does not exist.
    // ⚠ **MEASURED BEFORE REWRITING THEM**: `grep -c light backend/drawing/models.py` is 0.
    // There is no light record, no renderer and no shading pass anywhere in pdcad. This is a
    // 2D drafting replacement; the lights arrived with the lifted ribbon.
    // ⇒ Grey, with the reason. The rule this app keeps relearning: a control that appears to
    // work and does not is worse than one that says it does not.
    "vis.pointlight": { note: NO_LIGHTS },
    "vis.spotlight": { note: NO_LIGHTS },
    "vis.distant": { note: NO_LIGHTS },
    "vis.lightlist": { note: NO_LIGHTS },
    // ⚠⚠ **THREE OF THE SOLID TAB'S TWENTY WERE ALREADY BUILT AND ONE WAS ALREADY WIRED.**
    // `Model3D` exposes `pull` and `section`, and `pushpull` has been bound to the first since
    // the day it shipped. So PRESSPULL was live on one route and greyed on another, with a
    // reason that also claimed this app has no
    // modelling kernel — and `kernel.py` is OpenCascade and has been here since 2026-08-19.
    // Two false statements in one tooltip, on a command that works. AFTER the spread above, so
    // these win.
    // ⚠⚠ **THE TWELVE THAT WERE GREY BEHIND ONE PARAGRAPH, AND THE PARAGRAPH WAS RIGHT.** It
    // said: a solid built from a PROFILE, and this app stores solids as RECORDS a mesh is
    // generated from. It did — a wall has a length and a thickness — and the answer was never
    // to start baking meshes, it was to give a profile-built solid a RECORD of its own.
    // `DesignSolid` (migration 0047) is that record, `model3d.solids_3d` turns it into pieces,
    // and the viewer learned exactly two new shapes: a lathe and a ruled surface.
    // ⚠ The primitives take numbers in a dialog rather than three picks in the 3D view: this
    // viewport has no construction plane and no 3D object snap, so a three-pick BOX would be
    // three clicks that guess. Move and push/pull afterwards are real gestures and do the rest.
    ...Object.fromEntries((["box", "wedge", "pyramid", "cylinder", "cone", "sphere", "torus"])
      .map((k) => [`sol.${k}`, {
        run: () => c.makeBody(k),
        on: c.bodyKind === k,
        note: `a ${k} as a RECORD — it keeps its dimensions, so the schedule can price it and `
              + "the Boolean panel can cut with it",
      } as AcadHandler])),
    // ⚠ These four build from what is SELECTED on the plan, and the body then follows that
    // shape: move the polyline and the extrusion moves with it. That is the same rule the
    // booleans follow and the reason none of this is baked.
    ...Object.fromEntries((["extrude", "revolve", "loft", "sweep", "polysolid"])
      .map((k) => [`sol.${k}`, {
        run: () => c.makeBody(k),
        on: c.bodyKind === k,
        note: k === "revolve"
          ? "turns the selected shape about a vertical axis at the middle of the view — type "
            + "the sweep angle, so a half-round is 180"
          : k === "loft"
          ? "rules a solid through the selected shapes, evenly spaced over the height given — "
            + "this canvas is one plan, so the heights have to come from somewhere"
          : k === "sweep"
          ? "rules the selected shapes into a run over the height given. ⚠ Not yet a profile "
            + "along a picked path — that needs a path record"
          : `${k === "extrude" ? "extrudes" : "raises"} the selected shape to the height given, `
            + "and keeps following it",
      } as AcadHandler])),
    "sol.presspull": {
      run: () => c.go3d((a) => a.pull(!c.m3d?.tool?.pull)),
      on: cam3d && !!c.m3d?.tool?.pull,
      // ⚠ Says what it pushes. AutoCAD's PRESSPULL extrudes a bounded region into a NEW solid;
      // ours drags the face of something the plan already describes and writes the number back
      // to the record it came from — so a wall gets taller, it does not gain a lump. A drafter
      // who expected the AutoCAD meaning would try to pull a new box out of a floor.
      note: "drag a face in the 3D view. It edits the RECORD the model was generated from — a "
            + "wall's height, a carcass's depth — so the change survives a rebuild and reaches "
            + "the cutting list. It does not extrude a new solid out of nothing",
    },
    "sol.livesection": {
      run: () => c.go3d((a) => a.section(!c.m3d?.tool?.section)),
      on: cam3d && !!c.m3d?.tool?.section,
      note: "cuts the model live in the 3D view and fills the cut face",
    },
    // ⚠⚠ **THE CHECK IS OLDER THAN THE BUTTON.** `exact.clashes()` has done exact solid
    // intersection since the joinery work and only the test suite could reach it; INTERFERE is
    // that function pointed at the drawing (`interfere.py`). It is the ONE Solid-tab command
    // that needs no free-standing solid, because it only READS the bodies `model3d.build`
    // already makes — which is why it is live while its fourteen neighbours are not.
    "sol.interfere": { run: () => c.interfere(),
                       note: "INTERFERE ⚠ reports pieces sharing the same space, in litres. A "
                             + "part that sits INSIDE its host — a sash in its track, a jamb in "
                             + "its reveal — interferes and is not a fault; AutoCAD reports "
                             + "those too and leaves the judgement to you" },
    // ⚠⚠ **IT HAD NO HANDLER AT ALL, SO IT WAS GREY WITH NO REASON GIVEN.** Found by auditing
    // the commands the survey reported as refusing: this one refuses by falling off the end of
    // the table, which tells a drafter nothing. A missing entry and a considered refusal look
    // identical from the ribbon and are not the same thing.
    "sol.sectionplane": {
      note: "not built — a section plane cuts a 3D SOLID, and pdcad stores solids as opaque "
            + "ACIS blobs it carries faithfully and cannot slice. Draw the section as geometry, "
            + "or cut a viewport with MVIEW",
    },
    "sol.sectionblock": {
      note: "turns a live section into geometry. PDVN ▸ Section does this and goes further — it "
            + "makes a dimensioned section DRAWING from the cut, not a block",
    },
    // ⚠⚠⚠ **SEVEN BUTTONS HERE NAVIGATED TO `/design/render/<id>` AND THEREFORE SIGNED THE
    // DRAFTER OUT.** The block they were in argued, correctly for the DESIGN app, that a grey
    // tooltip saying "the daylight study lives on the render page" is one line short of being
    // the command — so it made them all navigate there. pdcad has no render page, no renderer,
    // no material library and no sun: it is a 2D drafting app whose output is a plotted PDF and
    // a DXF. The notes described a product, the runs went to a route this app does not serve,
    // and `nav` answered the only way it knew how.
    // ⚠ Kept as one table because the reason is one reason, said once.
    ...Object.fromEntries([
      "render", "renderwin", "renderenv", "renderpresets", "sun", "geoloc",
      "matbrowser", "mateditor", "matattach", "rendercrop",
    ].map((k) => [`vis.${k}`, {
      note: "photorealistic rendering, materials, lighting and a sun study. pdcad is a 2D "
          + "drafting app — it has no 3D geometry to render and no renderer. Its output is a "
          + "plotted PDF at true scale and a DXF",
    } as AcadHandler])),

    "col.share": { note: WHY["col.share"] },
    "col.trace": { note: WHY["col.trace"] },
    "col.markupimport": { note: WHY["col.markupimport"] },
    "col.compare": { note: WHY["col.compare"] },
    "col.xrefcompare": { note: WHY["col.xrefcompare"] },
    "addin.appmanager": { note: WHY["addin.appmanager"] },
    "addin.appstore": { note: WHY["addin.appstore"] },
    "addin.plugins": { note: WHY["addin.plugins"] },
    // out.preview / out.batchplot / out.pagesetup are LIVE — see the Plot dialog above.
    "out.plottermanager": { note: "there is no plotter — a plot is a PDF file" },
    // ⚠ It said "pens are set per layer" and that stopped being the whole truth the day the
    // Properties panel started setting them per OBJECT. A named plot-style TABLE is still not a
    // thing here — the pen IS the colour and weight, there is no ctb mapping one to the other —
    // and that is the honest reason, where "per layer" was merely where they used to live.
    "out.plotstyle": { note: "there is no named plot-style table (.ctb) — the pen a line is "
                           + "drawn with IS the pen it plots with. Set it per LAYER in the Layer "
                           + "Properties Manager, or per OBJECT on the Properties panel" },
    "out.viewdetails": { note: WHY["out.viewdetails"] },
    // ⚠ These four are AutoCAD's OWN export formats and none of them is one this office sends.
    // Each names what to send instead, by tab, rather than saying "PDF and DXF are built" —
    // which was the true half of a sentence whose false half was "and nothing else is".
    "out.exportdwf": { note: "DWF is Autodesk's own review format. Send a PDF (Output ▸ Export "
                             + "PDF) or the DWG (PDVN ▸ Deliverables)" },
    "out.exportdgn": { note: "DGN is MicroStation's. Send the DWG or the DXF — PDVN ▸ "
                             + "Deliverables and Output ▸ Export" },
    // ⚠⚠ THIS WAS ONE OF THE MOST MISLEADING GREYS IN THE SHELL. `IN_3D` promised the 3D view
    // had an FBX and an STL export; it had neither. The paragraph that replaced it then said
    // "COLLADA and OBJ and nothing else" — true when written and false the day STL landed, one
    // screen below. A note that lists what exists dates the moment the list grows.
    "out.exportfbx": { note: "FBX carries a 3D model, and this app is 2D. What it writes is DXF and a plotted PDF at true scale" },
    // ⚠⚠ **ITS OWN NOTE SAID THE REASON WAS "nobody here 3D-prints a fit-out, not because it
    // would be hard" — and that is not a reason a BUTTON can give.** Whether anyone prints one
    // is the drafter's business, not the app's; the stated limitation ("no materials and no
    // units") is equally true of OBJ, which has been offered beside it all along. So the
    // refusal had outlived its reason twice over.
    // ⚠ BINARY, not ASCII — an ASCII STL is roughly six times the size for identical geometry,
    // and a plan is already 29,000 triangles. The unit goes in the 80-byte header, which is the
    // only place STL has for one, and the normals are computed rather than left at zero because
    // plenty of slicers trust the stored normal over the winding.
    "out.exportstl": {
      run: () => { void c.exportAs("stl"); },
      note: "binary STL — the format a CNC shop or a printer asks for by name. ⚠ Carries no "
          + "material and no unit: the header says MILLIMETRES and the reader has to be told "
          + "the same, exactly as with OBJ",
    },
    // ⚠⚠ **`ON_SHEET` ANSWERED A QUESTION THIS COMMAND DOES NOT ASK.** It explains that a
    // named sheet SET spanning several drawings is not built — true, and nothing to do with
    // EXPORTLAYOUT, which writes THIS layout to a NEW drawing with the paper contents and
    // everything seen through each viewport flattened into model space. Found by auditing the
    // commands the survey reported as refusing: a refusal that answers a different question
    // reads as considered and teaches the wrong thing.
    // ⚠⚠ **BUILT 2026-09-04.** The grey before this said `ON_SHEET` — "a named sheet SET is not
    // built" — which answers a question EXPORTLAYOUT never asks; the audit corrected the note
    // and this replaces it with the command. CHSPACE was the geometry half all along.
    "out.exportlayout": {
      run: () => {
        const r = A.exportLayout?.();
        if (!r) { c.warn("EXPORTLAYOUT is not wired to this canvas."); return; }
        if (r.why) { c.warn(r.why); return; }
      },
      note: "writes the OPEN layout to a NEW drawing, with the sheet and everything seen "
            + "through its viewport flattened into model space at the viewport's scale. "
            + "⚠ Objects crossing the viewport border are kept WHOLE — AutoCAD cuts them, and "
            + "the count of them is reported",
    },
    "out.exportsetup": { note: WHY["out.exportsetup"] },

    // ── odds and ends the menus reach ───────────────────────────────────────────────────
    "file.new.sheetset": { note: ON_SHEET },
    "file.open.sheetset": { note: ON_SHEET },
    "file.open.dgn": { note: WHY["file.open.dgn"] },
    "file.saveas.template": { note: WHY["file.saveas.template"] },
    // ⚠ DWG is deliberately not offered anywhere in this shell. The server CAN write one
    // (`fmt=dwg`, converted from the DXF by ODAFileConverter) but /usr/bin/ODAFileConverter is
    // not installed on this machine, so the request fails. A download that errors is worse than
    // one that was never offered. (The drawing's own data page still shows a DWG button — see
    // the roadmap; that one is live and cannot work here.)
    // ⚠ IFC, Collada and OBJ are 3D exchange formats and this app is 2D — see `out.exportfbx`.
    // ⚠⚠ And the DWG line was out of date: the ODA File Converter IS installed here now and
    // confirms this app's DXF opens correctly. It is a VERIFICATION tool, not a shipping one —
    // its licence forbids distributing it with another product — so DXF stays the written format.
    "file.saveas.other": { note: "DXF is written, on Output ▸ Export, and it opens in AutoCAD. DWG is read but not written: the only free writer produces a file a second implementation rejects" },
    "file.etransmit": { note: WHY["file.etransmit"] },
    "file.archive": { note: WHY["file.archive"] },
    "file.audit": { note: WHY["file.audit"] },
    "file.recover": { note: WHY["file.recover"] },
    "file.units": { on: true, note: "the drawing is in millimetres" },
    // ⚠ DWGPROPS is the drawing's own record, and in pdcad that is the top of the Properties
    // palette with nothing selected — name, source file, DXF version, object counts and extents.
    "file.props": { run: () => c.showPalette("prop.palette", true),
                    note: "the drawing's name, source, DXF version, counts and extents — top of the Properties palette" },
    "format.limits": { note: WHY["format.limits"] },
    "win.close": { note: "closes the drawing without leaving the app. Not built — File ▸ Open swaps drawings directly, and Exit signs out" },
    "win.closeall": { note: "one drawing is open at a time here, so there is no set of them to close" },
    "win.cascade": { note: "one drawing is open at a time here" },
    "win.tileh": { note: "one drawing is open at a time here" },
    "win.tilev": { note: "one drawing is open at a time here" },
    "ui.lock.toolbars": { note: WHY["ui.lock.toolbars"] },
    "ui.lock.windows": { note: WHY["ui.lock.windows"] },
    "ui.lock.all": { note: WHY["ui.lock.all"] },
    "qat.more": { note: WHY["qat.more"] },
    "qat.below": { note: WHY["qat.below"] },
    "cloud.open": { note: WHY["cloud.open"] },
    "cloud.save": { note: "the drawing saves to the PDVN server — Save (QSAVE)" },
    "help.help": { note: WHY["help.help"] },
    "help.newfeatures": { note: WHY["help.newfeatures"] },
    "help.support": { note: WHY["help.support"] },
    "help.about": { note: "PDVN design studio, AutoCAD interface" },

    // ⚠⚠ **`app.options` IS NOT HERE, AND THAT IS THE FIX FOR A BUG THIS FILE CAUSED.** It was,
    // wired straight to the studio's command palette — and because this table is merged OVER the
    // chrome's own, it silently replaced the chrome's Options handler. The Options dialog could
    // then never open, so its colour theme, its window-element ticks and its toolbar checkboxes
    // were all unreachable: a whole dialog of controls that looked built and could not be shown.
    // The chrome owns `app.options`; the dialog's Profiles tab is what calls `openPalette`.

    // ⚠ The QAT is fixed here, and its ▾ menu says so by ticking every row and greying it,
    // rather than showing seven unticked boxes for buttons that are plainly on screen.
    ...["new", "open", "save", "saveas", "plot", "undo", "redo"]
      .reduce((o, k) => {
        o[`qat.${k}`] = { on: true, note: "the Quick Access Toolbar is fixed in this shell" };
        return o;
      }, {} as Record<string, AcadHandler>),
  };
}
